import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Camera, Mic, MapPin, Image, Bell, Monitor, Cpu,
  Shield, Users, Search, RefreshCw, ChevronRight, ChevronLeft,
  Lock, Unlock, Key, AlertTriangle, CheckCircle2,
  XCircle, Activity, ExternalLink,
  Square, ClipboardList, Settings, Phone, Mail,
  Maximize, Download, Compass, Battery, Wifi, Globe, Smartphone,
  Sliders, ArrowLeft, Clock, Check, Volume2, VolumeX, Radio, Play, Pause,
  Trash2, Send, Maximize2, Minimize2, Video, User, FlipHorizontal
} from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MainLayout } from "../components/MainLayout";
import {
  adminGetUsersWithPermissions, adminGetUserPermissions, adminGetUserPermissionAudit,
  adminGetUserLocation, adminGetUserLocationHistory, adminCheckUserPin, adminSetUserPin,
  adminVerifyUserPin, adminGetUserSessions, adminRequestSession, adminEndSession,
  adminGetDeviceAccessAudit, adminOverridePermission,
  adminGetUserContacts, adminGetUserDeviceInfo,
  adminGetLocationSettings, adminSaveLocationSettings,
  adminGetSessionSignal, adminSendSessionSignal,
  adminGetNotificationSettings, adminSaveNotificationSettings,
  adminGetUserNotificationList, adminSendUserNotification, adminDeleteNotification
} from "../api/deviceAccess";

// Fix leaflet default icon URLs in Vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const iosPuckIcon = L.divIcon({
  className: "ios-puck-container",
  html: `<div style="position:relative;width:24px;height:24px;">
    <div style="position:absolute;inset:0;border-radius:50%;background:rgba(0,122,255,0.25);animation:iosPuckPing 2s cubic-bezier(0,0,0.2,1) infinite;"></div>
    <div style="position:absolute;top:4px;left:4px;width:16px;height:16px;border-radius:50%;background:#007aff;border:2.5px solid #fff;box-shadow:0 2px 8px rgba(0,122,255,0.6);"></div>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const PERM_TYPES = [
  { type: "camera", label: "Camera", color: "#3b82f6", icon: Camera, desc: "Real-time video inspection" },
  { type: "microphone", label: "Microphone", color: "#8b5cf6", icon: Mic, desc: "Audio communication stream" },
  { type: "location", label: "Location", color: "#10b981", icon: MapPin, desc: "GPS tracking & live history" },
  { type: "contacts", label: "Contacts", color: "#6366f1", icon: Phone, desc: "Device address book directory" },
  { type: "device_info", label: "Device Info", color: "#64748b", icon: Cpu, desc: "Hardware & system telemetry" },
  { type: "screen_share", label: "Screen Share", color: "#06b6d4", icon: Monitor, desc: "Live display mirror & capture" },
  { type: "media", label: "Media Access", color: "#ec4899", icon: Image, desc: "User-uploaded media assets" },
  { type: "notifications", label: "Notifications", color: "#f59e0b", icon: Bell, desc: "Push security alert triggers" },
];

const STATUS_BADGE = {
  granted: { bg: "rgba(16,185,129,0.14)", border: "rgba(16,185,129,0.3)", text: "#10b981", label: "Granted" },
  denied: { bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.3)", text: "#ef4444", label: "Denied" },
  not_requested: { bg: "rgba(100,116,139,0.12)", border: "rgba(100,116,139,0.2)", text: "#94a3b8", label: "Not Set" },
  revoked: { bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.25)", text: "#f59e0b", label: "Revoked" },
};

const SESSION_TYPE_CONFIG = {
  camera: { label: "Camera Session", color: "#3b82f6" },
  microphone: { label: "Audio Session", color: "#8b5cf6" },
  screen_share: { label: "Screen Share", color: "#06b6d4" },
  location_view: { label: "Location View", color: "#10b981" },
  media_view: { label: "Media View", color: "#ec4899" },
};

const SESSION_STATUS_BADGE = {
  pending: { bg: "rgba(245,158,11,0.14)", text: "#f59e0b", label: "Pending User Auth" },
  active: { bg: "rgba(16,185,129,0.16)", text: "#10b981", label: "Live Streaming" },
  ended: { bg: "rgba(100,116,139,0.12)", text: "#94a3b8", label: "Ended" },
  denied: { bg: "rgba(239,68,68,0.12)", text: "#ef4444", label: "Declined by User" },
};

const ROLE_COLORS = {
  SUPER_ADMIN: "#ef4444",
  ADMIN: "#f59e0b",
  USER: "#3b82f6",
  TEAM_OWNER: "#8b5cf6",
  TEAM_MEMBER: "#10b981"
};

const getUserInitials = (email) => {
  if (!email) return "?";
  const p = email.split("@")[0].split(/[._-]/);
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : p[0].slice(0, 2).toUpperCase();
};

const getAvatarColor = (email) => {
  const colors = ["#007aff", "#5856d6", "#34c759", "#ff9500", "#af52de", "#5ac8fa", "#ff2d55", "#30b0c7"];
  let h = 0;
  for (const c of (email || "")) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
  return colors[Math.abs(h) % colors.length];
};

const timeAgo = (d) => {
  if (!d) return "Never";
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return m + "m ago";
  const h = Math.floor(m / 60);
  if (h < 24) return h + "h ago";
  return Math.floor(h / 24) + "d ago";
};

const fmtDate = (d) => d ? new Date(d).toLocaleString() : "—";

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.not_requested;
  return (
    <span style={{
      fontSize: "0.68rem",
      fontWeight: 700,
      padding: "2.5px 8px",
      borderRadius: 12,
      background: cfg.bg,
      color: cfg.text,
      border: "1px solid " + cfg.border,
      letterSpacing: "0.02em"
    }}>
      {cfg.label}
    </span>
  );
}

// ─── Permission Card (Interactive) ────────────────────────────────────────────
function PermCard({ perm, onClick, onOverride, isUnlocked }) {
  const cfg = PERM_TYPES.find(p => p.type === perm.permission_type) || {};
  const { color = "#64748b", label = perm.permission_type, icon: Icon = Shield, desc = "" } = cfg;
  const isGranted = perm.status === "granted";

  return (
    <div
      onClick={isGranted ? onClick : undefined}
      style={{
        background: "var(--bg-surface, rgba(255,255,255,0.03))",
        border: "1px solid " + (isGranted ? "rgba(16,185,129,0.25)" : perm.status === "denied" ? "rgba(239,68,68,0.18)" : "var(--border-color, rgba(255,255,255,0.08))"),
        borderRadius: 16,
        padding: "15px 16px",
        cursor: isGranted ? "pointer" : "default",
        transition: "all 0.18s ease-in-out",
        boxShadow: isGranted ? "0 4px 14px rgba(0,0,0,0.1)" : "none",
        position: "relative",
        overflow: "hidden"
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
          <div style={{
            width: 38,
            height: 38,
            borderRadius: 11,
            background: color + "18",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}>
            <Icon size={19} color={color} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontWeight: 600,
              fontSize: "0.88rem",
              color: "var(--text-primary)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}>
              {label}
            </div>
            <div style={{
              fontSize: "0.71rem",
              color: "var(--text-secondary)",
              marginTop: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}>
              {desc}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
          <StatusBadge status={perm.status} />
          {isGranted && (
            <div style={{
              fontSize: "0.68rem",
              color: "#007aff",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 2,
              background: "rgba(0,122,255,0.08)",
              padding: "2px 6px",
              borderRadius: 6
            }}>
              View <ChevronRight size={10} />
            </div>
          )}
        </div>
      </div>

      <div style={{ fontSize: "0.69rem", color: "var(--text-secondary)", display: "flex", justifyContent: "space-between", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 8, marginTop: 6 }}>
        <span>{perm.granted_at ? "Active: " + timeAgo(perm.granted_at) : "Status: " + (perm.status || "not requested")}</span>
        {perm.user_agent && <span style={{ opacity: 0.7 }}>Web</span>}
      </div>

      {isUnlocked && onOverride && (
        <div style={{ display: "flex", gap: 6, marginTop: 10 }} onClick={e => e.stopPropagation()}>
          {perm.status !== "granted" && (
            <button
              onClick={() => onOverride(perm.permission_type, "granted")}
              style={{
                flex: 1,
                height: 28,
                borderRadius: 8,
                background: "rgba(16,185,129,0.12)",
                color: "#10b981",
                border: "1px solid rgba(16,185,129,0.3)",
                fontSize: "0.72rem",
                fontWeight: 600,
                cursor: "pointer"
              }}
            >
              Override Grant
            </button>
          )}
          {perm.status !== "denied" && (
            <button
              onClick={() => onOverride(perm.permission_type, "denied")}
              style={{
                flex: 1,
                height: 28,
                borderRadius: 8,
                background: "rgba(239,68,68,0.1)",
                color: "#ef4444",
                border: "1px solid rgba(239,68,68,0.25)",
                fontSize: "0.72rem",
                fontWeight: 600,
                cursor: "pointer"
              }}
            >
              Override Deny
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Strict PIN Gate & iOS Keypad Lock Screen ───────────────────────────────────
const IOS_KEYPAD = [
  { num: "1", letters: "" },
  { num: "2", letters: "A B C" },
  { num: "3", letters: "D E F" },
  { num: "4", letters: "G H I" },
  { num: "5", letters: "J K L" },
  { num: "6", letters: "M N O" },
  { num: "7", letters: "P Q R S" },
  { num: "8", letters: "T U V" },
  { num: "9", letters: "W X Y Z" },
];

function PinLockScreen({ userId, userEmail, onUnlock, remainingAttempts, lockedUntil }) {
  const [digits, setDigits] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [lockoutTimeLeft, setLockoutTimeLeft] = useState(0);
  const [isShaking, setIsShaking] = useState(false);

  // Lockout countdown timer
  useEffect(() => {
    if (!lockedUntil) {
      setLockoutTimeLeft(0);
      return;
    }
    const updateCountdown = () => {
      const remaining = Math.max(0, Math.ceil((new Date(lockedUntil).getTime() - Date.now()) / 1000));
      setLockoutTimeLeft(remaining);
    };
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [lockedUntil]);

  const verifyPin = async (code) => {
    if (lockoutTimeLeft > 0 || loading) return;
    setLoading(true);
    setErrorMsg("");
    try {
      const r = await adminVerifyUserPin(userId, code);
      if (r.verified) {
        onUnlock();
      } else {
        setIsShaking(true);
        setTimeout(() => {
          setIsShaking(false);
          setDigits("");
        }, 500);
        if (r.lockedUntil) {
          setErrorMsg("Account locked for 5 minutes due to 3 failed attempts.");
        } else {
          setErrorMsg(`Incorrect passcode. ${r.remainingAttempts ?? 0} attempt${(r.remainingAttempts ?? 0) === 1 ? "" : "s"} remaining.`);
        }
      }
    } catch (err) {
      setIsShaking(true);
      setTimeout(() => {
        setIsShaking(false);
        setDigits("");
      }, 500);
      setErrorMsg(err?.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDigit = (d) => {
    if (lockoutTimeLeft > 0 || loading) return;
    if (digits.length < 4) {
      const next = digits + d;
      setDigits(next);
      setErrorMsg("");
      if (next.length === 4) {
        verifyPin(next);
      }
    }
  };

  const handleBackspace = () => {
    if (lockoutTimeLeft > 0 || loading) return;
    setDigits(prev => prev.slice(0, -1));
    setErrorMsg("");
  };

  // Keyboard navigation support (numbers 0-9 & Backspace)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lockoutTimeLeft > 0 || loading) return;
      if (/^[0-9]$/.test(e.key)) {
        handleDigit(e.key);
      } else if (e.key === "Backspace") {
        handleBackspace();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [digits, lockoutTimeLeft, loading]);

  const formatLockout = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const btnSize = window.innerWidth <= 480 ? 60 : 76;
  const numSize = window.innerWidth <= 480 ? "1.55rem" : "1.85rem";
  const letSize = window.innerWidth <= 480 ? "0.5rem" : "0.56rem";
  const gapH = window.innerWidth <= 480 ? "10px 16px" : "14px 24px";

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: window.innerWidth <= 480 ? "360px" : "440px",
      padding: window.innerWidth <= 480 ? "18px 12px" : "28px 20px",
      textAlign: "center",
      userSelect: "none"
    }}>
      <style>{`
        @keyframes iosPasscodeShake {
          0%, 100% { transform: translateX(0); }
          15%, 45%, 75% { transform: translateX(-12px); }
          30%, 60%, 90% { transform: translateX(12px); }
        }
      `}</style>

      <div style={{
        width: 62,
        height: 62,
        borderRadius: "50%",
        background: lockoutTimeLeft > 0 ? "rgba(255,59,48,0.14)" : "rgba(0,122,255,0.12)",
        border: lockoutTimeLeft > 0 ? "1px solid rgba(255,59,48,0.3)" : "1px solid rgba(0,122,255,0.25)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 14,
        boxShadow: lockoutTimeLeft > 0 ? "0 8px 24px rgba(255,59,48,0.2)" : "0 8px 24px rgba(0,122,255,0.2)"
      }}>
        {lockoutTimeLeft > 0 ? <AlertTriangle size={28} color="#ff3b30" /> : <Lock size={28} color="#007aff" />}
      </div>

      <h2 style={{ margin: "0 0 4px", fontSize: window.innerWidth <= 480 ? "1.05rem" : "1.2rem", fontWeight: 600, letterSpacing: "-0.3px", color: "var(--text-primary)" }}>
        {lockoutTimeLeft > 0 ? "Security Lockout Active" : "Enter Passcode"}
      </h2>
      <p style={{ margin: "0 0 20px", fontSize: "0.78rem", color: "var(--text-secondary)", maxWidth: 300, lineHeight: 1.4 }}>
        {lockoutTimeLeft > 0
          ? "3 incorrect attempts. Locked for 5 minutes."
          : `Enter the admin passcode for ${userEmail}`}
      </p>

      {lockoutTimeLeft > 0 ? (
        <div style={{
          background: "rgba(255,59,48,0.08)",
          border: "1px solid rgba(255,59,48,0.25)",
          borderRadius: 18,
          padding: "20px 28px",
          marginBottom: 16
        }}>
          <div style={{ fontSize: "2.2rem", fontWeight: 700, color: "#ff3b30", fontFamily: "monospace", letterSpacing: "1px" }}>
            {formatLockout(lockoutTimeLeft)}
          </div>
          <div style={{ fontSize: "0.78rem", color: "#fca5a5", marginTop: 6 }}>Try again when countdown completes</div>
        </div>
      ) : (
        <>
          {/* iOS Passcode Dots */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 18,
            marginBottom: errorMsg ? 14 : 28,
            animation: isShaking ? "iosPasscodeShake 0.45s cubic-bezier(0.36, 0.07, 0.19, 0.97) both" : "none"
          }}>
            {[0, 1, 2, 3].map(idx => {
              const filled = digits.length > idx;
              return (
                <div
                  key={idx}
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    border: isShaking
                      ? "1.5px solid #ff3b30"
                      : filled
                        ? "1.5px solid #007aff"
                        : "1.5px solid var(--text-secondary, rgba(255,255,255,0.35))",
                    background: isShaking
                      ? "#ff3b30"
                      : filled
                        ? "#007aff"
                        : "transparent",
                    transform: filled ? "scale(1.15)" : "scale(1)",
                    boxShadow: filled && !isShaking ? "0 0 10px rgba(0,122,255,0.4)" : "none",
                    transition: "all 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
                  }}
                />
              );
            })}
          </div>

          {errorMsg && (
            <div style={{
              color: "#ff3b30",
              fontSize: "0.78rem",
              fontWeight: 500,
              marginBottom: 18,
              maxWidth: 320,
              background: "rgba(255,59,48,0.08)",
              border: "1px solid rgba(255,59,48,0.2)",
              padding: "6px 14px",
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6
            }}>
              <AlertTriangle size={13} style={{ flexShrink: 0 }} />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* iOS Keypad Grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: `repeat(3, ${btnSize}px)`,
            gap: gapH,
            justifyContent: "center",
            maxWidth: 290,
            width: "100%"
          }}>
            {IOS_KEYPAD.map(k => (
              <button
                key={k.num}
                type="button"
                onClick={() => handleDigit(k.num)}
                disabled={loading}
                style={{
                  width: btnSize,
                  height: btnSize,
                  borderRadius: "50%",
                  background: "var(--bg-surface, rgba(255,255,255,0.06))",
                  border: "1px solid var(--border-color, rgba(255,255,255,0.1))",
                  color: "var(--text-primary)",
                  cursor: loading ? "default" : "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  outline: "none",
                  userSelect: "none",
                  WebkitTapHighlightColor: "transparent",
                  transition: "transform 0.08s ease, background 0.12s ease",
                  padding: 0
                }}
                onMouseDown={e => { e.currentTarget.style.transform = "scale(0.92)"; e.currentTarget.style.background = "rgba(255,255,255,0.18)"; }}
                onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = "var(--bg-surface, rgba(255,255,255,0.06))"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = "var(--bg-surface, rgba(255,255,255,0.06))"; }}
                onTouchStart={e => { e.currentTarget.style.transform = "scale(0.92)"; e.currentTarget.style.background = "rgba(255,255,255,0.18)"; }}
                onTouchEnd={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = "var(--bg-surface, rgba(255,255,255,0.06))"; }}
              >
                <span style={{ fontSize: numSize, fontWeight: 300, lineHeight: 1, letterSpacing: "-0.5px" }}>
                  {k.num}
                </span>
                {k.letters && (
                  <span style={{ fontSize: letSize, fontWeight: 600, letterSpacing: "1.8px", color: "var(--text-secondary)", textTransform: "uppercase", marginTop: 2 }}>
                    {k.letters}
                  </span>
                )}
              </button>
            ))}

            {/* Row 4: Spacer / 0 / Delete */}
            <div style={{ width: btnSize, height: btnSize }} />

            <button
              type="button"
              onClick={() => handleDigit("0")}
              disabled={loading}
              style={{
                width: btnSize,
                height: btnSize,
                borderRadius: "50%",
                background: "var(--bg-surface, rgba(255,255,255,0.06))",
                border: "1px solid var(--border-color, rgba(255,255,255,0.1))",
                color: "var(--text-primary)",
                cursor: loading ? "default" : "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                outline: "none",
                userSelect: "none",
                WebkitTapHighlightColor: "transparent",
                transition: "transform 0.08s ease, background 0.12s ease",
                padding: 0
              }}
              onMouseDown={e => { e.currentTarget.style.transform = "scale(0.92)"; e.currentTarget.style.background = "rgba(255,255,255,0.18)"; }}
              onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = "var(--bg-surface, rgba(255,255,255,0.06))"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = "var(--bg-surface, rgba(255,255,255,0.06))"; }}
              onTouchStart={e => { e.currentTarget.style.transform = "scale(0.92)"; e.currentTarget.style.background = "rgba(255,255,255,0.18)"; }}
              onTouchEnd={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = "var(--bg-surface, rgba(255,255,255,0.06))"; }}
            >
              <span style={{ fontSize: numSize, fontWeight: 300, lineHeight: 1, letterSpacing: "-0.5px" }}>
                0
              </span>
              <span style={{ fontSize: letSize, fontWeight: 600, letterSpacing: "1px", color: "var(--text-secondary)", marginTop: 2 }}>
                +
              </span>
            </button>

            <button
              type="button"
              onClick={handleBackspace}
              disabled={loading || digits.length === 0}
              style={{
                width: btnSize,
                height: btnSize,
                borderRadius: "50%",
                background: "transparent",
                border: "none",
                color: "var(--text-primary)",
                fontSize: window.innerWidth <= 480 ? "0.82rem" : "0.95rem",
                fontWeight: 400,
                cursor: digits.length > 0 ? "pointer" : "default",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: digits.length > 0 ? 1 : 0,
                transition: "opacity 0.15s ease",
                outline: "none",
                userSelect: "none"
              }}
            >
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── iOS Right Sidebar Drawer Container ─────────────────────────────────────────
function IOSRightSidebarDrawer({
  isOpen,
  onClose,
  isMobile,
  title,
  subtitle,
  icon: Icon,
  iconColor = "#007aff",
  headerActions,
  children
}) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0, 0, 0, 0.45)",
        backdropFilter: "blur(5px)",
        display: "flex",
        justifyContent: "flex-end",
        animation: "iosFadeIn 0.2s ease"
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes iosFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes iosSlideRightIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
      <div
        style={{
          width: isMobile ? "100%" : "540px",
          maxWidth: "100%",
          height: "100%",
          boxSizing: "border-box",
          background: "var(--bg-card, #1e293b)",
          borderLeft: isMobile ? "none" : "1px solid var(--border-color)",
          boxShadow: "-16px 0 45px rgba(0,0,0,0.55)",
          display: "flex",
          flexDirection: "column",
          animation: "iosSlideRightIn 0.28s cubic-bezier(0.16, 1, 0.3, 1)",
          paddingBottom: isMobile ? "110px" : 0,
          overflow: "hidden"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* iOS Top Navigation Bar */}
        <div style={{
          padding: isMobile ? "12px 14px" : "16px 20px",
          borderBottom: "1px solid var(--border-color)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          background: "var(--bg-surface, rgba(255,255,255,0.02))",
          boxSizing: "border-box",
          width: "100%",
          minWidth: 0
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12, minWidth: 0, flex: 1 }}>
            {isMobile && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Back"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: "rgba(0,122,255,0.1)",
                  border: "none",
                  color: "#007aff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  flexShrink: 0,
                  marginRight: 2
                }}
              >
                <ChevronLeft size={18} />
              </button>
            )}
            {Icon && (
              <div style={{
                width: isMobile ? 32 : 36,
                height: isMobile ? 32 : 36,
                borderRadius: 10,
                background: `${iconColor}18`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0
              }}>
                <Icon size={isMobile ? 16 : 19} color={iconColor} />
              </div>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <h3 style={{
                margin: 0,
                fontSize: isMobile ? "0.92rem" : "1.02rem",
                fontWeight: 700,
                color: "var(--text-primary)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}>
                {title}
              </h3>
              {subtitle && (
                <div style={{
                  fontSize: isMobile ? "0.68rem" : "0.72rem",
                  color: "var(--text-secondary)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  marginTop: 1
                }}>
                  {subtitle}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {headerActions}
            {!isMobile && (
              <button
                type="button"
                onClick={onClose}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: "var(--bg-surface, rgba(255,255,255,0.06))",
                  color: "var(--text-secondary)",
                  border: "1px solid var(--border-color)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.88rem"
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Scrollable Content Viewport */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          boxSizing: "border-box"
        }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Set PIN Drawer Panel ───────────────────────────────────────────────────────
function SetPinPanel({ userId, onSuccess, isMobile }) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pin.length < 4) { setError("Passcode must be at least 4 digits"); return; }
    if (pin !== confirmPin) { setError("Passcodes do not match"); return; }
    setLoading(true); setError("");
    try {
      await adminSetUserPin(userId, pin);
      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to set PIN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: isMobile ? "20px 14px" : "28px 24px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      width: "100%",
      boxSizing: "border-box"
    }}>
      <div style={{
        width: 54, height: 54, borderRadius: "50%",
        background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)",
        display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14
      }}>
        <Key size={24} color="#8b5cf6" />
      </div>
      <h3 style={{ margin: "0 0 6px", fontSize: isMobile ? "1.05rem" : "1.15rem", fontWeight: 700, color: "var(--text-primary)" }}>
        Configure Admin Passcode
      </h3>
      <p style={{ margin: "0 0 20px", fontSize: "0.8rem", color: "var(--text-secondary)", lineHeight: 1.45, maxWidth: 320 }}>
        Set a 4-8 digit passcode to gate profile telemetry. 3 wrong attempts will trigger a 5-minute lockout.
      </p>

      <form onSubmit={handleSubmit} style={{ width: "100%", maxWidth: 320, display: "flex", flexDirection: "column", gap: 12, boxSizing: "border-box" }}>
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={8}
          value={pin}
          onChange={e => { setPin(e.target.value.replace(/\D/g, "")); setError(""); }}
          placeholder="New Passcode (4-8 digits)"
          style={{
            width: "100%", height: 44, borderRadius: 12, border: "1px solid var(--border-color)",
            background: "var(--bg-surface, rgba(255,255,255,0.04))", color: "var(--text-primary)",
            fontSize: "1.2rem", textAlign: "center", letterSpacing: "0.25em", outline: "none", boxSizing: "border-box"
          }}
        />
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={8}
          value={confirmPin}
          onChange={e => { setConfirmPin(e.target.value.replace(/\D/g, "")); setError(""); }}
          placeholder="Confirm Passcode"
          style={{
            width: "100%", height: 44, borderRadius: 12, border: "1px solid var(--border-color)",
            background: "var(--bg-surface, rgba(255,255,255,0.04))", color: "var(--text-primary)",
            fontSize: "1.2rem", textAlign: "center", letterSpacing: "0.25em", outline: "none", boxSizing: "border-box"
          }}
        />

        {error && (
          <div style={{
            color: "#ff3b30", fontSize: "0.76rem", background: "rgba(255,59,48,0.1)",
            padding: "8px 12px", borderRadius: 10, border: "1px solid rgba(255,59,48,0.25)"
          }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || pin.length < 4 || pin !== confirmPin}
          style={{
            height: 42, borderRadius: 12,
            background: "linear-gradient(135deg, #007aff, #5856d6)", color: "#fff",
            border: "none", fontWeight: 600, fontSize: "0.9rem", cursor: "pointer",
            marginTop: 4, opacity: loading || pin.length < 4 || pin !== confirmPin ? 0.6 : 1
          }}
        >
          {loading ? "Saving Passcode..." : "Save Passcode"}
        </button>
      </form>
    </div>
  );
}

// ─── Location Tracking Settings Drawer Panel ────────────────────────────────────
function LocationSettingsPanel({ onSaved, onBack, isMobile }) {
  const [syncInterval, setSyncInterval] = useState(60);
  const [retentionDays, setRetentionDays] = useState(5);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetLocationSettings()
      .then(s => {
        if (s) {
          setSyncInterval(s.sync_interval_mins || 60);
          setRetentionDays(s.retention_days || 5);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminSaveLocationSettings({
        syncIntervalMinutes: syncInterval,
        retentionDays: retentionDays,
        trackingActive: true
      });
      onSaved({ syncInterval, retentionDays });
      if (onBack) onBack();
    } catch {
      alert("Failed to save location settings");
    } finally {
      setSaving(false);
    }
  };

  const intervals = [
    { value: 15, label: "15 Mins" },
    { value: 30, label: "30 Mins" },
    { value: 60, label: "1 Hour" },
    { value: 120, label: "2 Hours" },
    { value: 360, label: "6 Hours" }
  ];

  const retentions = [
    { value: 1, label: "1 Day" },
    { value: 3, label: "3 Days" },
    { value: 5, label: "5 Days" },
    { value: 7, label: "7 Days" },
    { value: 30, label: "30 Days" }
  ];

  return (
    <div style={{
      padding: isMobile ? "12px 10px" : "20px",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      width: "100%",
      boxSizing: "border-box"
    }}>
      <div style={{
        background: "var(--bg-surface, rgba(255,255,255,0.03))",
        border: "1px solid var(--border-color)",
        borderRadius: 16,
        padding: isMobile ? "14px 10px" : "18px",
        width: "100%",
        boxSizing: "border-box"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <Sliders size={18} color="#10b981" style={{ flexShrink: 0 }} />
          <h4 style={{ margin: 0, fontSize: isMobile ? "0.88rem" : "0.95rem", fontWeight: 700, color: "var(--text-primary)", wordBreak: "break-word" }}>
            Location Tracking & Retention
          </h4>
        </div>
        <p style={{ margin: "0 0 16px", fontSize: isMobile ? "0.74rem" : "0.78rem", color: "var(--text-secondary)", lineHeight: 1.45 }}>
          Configure telemetry sync frequency and how many days location checkpoints are kept before automatic purge.
        </p>

        {loading ? (
          <div style={{ padding: "20px 0", textAlign: "center", color: "var(--text-secondary)", fontSize: "0.82rem" }}>
            Loading settings...
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%", boxSizing: "border-box" }}>
            <div style={{ width: "100%", boxSizing: "border-box" }}>
              <label style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--text-primary)", display: "block", marginBottom: 8 }}>
                Sync Frequency
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6, width: "100%", boxSizing: "border-box" }}>
                {intervals.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSyncInterval(opt.value)}
                    style={{
                      height: 36, borderRadius: 10,
                      border: syncInterval === opt.value ? "1.5px solid #10b981" : "1px solid var(--border-color)",
                      background: syncInterval === opt.value ? "rgba(16,185,129,0.15)" : "var(--bg-card)",
                      color: syncInterval === opt.value ? "#10b981" : "var(--text-secondary)",
                      fontSize: "0.74rem", fontWeight: 600, cursor: "pointer",
                      minWidth: 0, boxSizing: "border-box"
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ width: "100%", boxSizing: "border-box" }}>
              <label style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--text-primary)", display: "block", marginBottom: 8 }}>
                Auto-Delete History After
              </label>
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
                gap: 6,
                width: "100%",
                boxSizing: "border-box"
              }}>
                {retentions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setRetentionDays(opt.value)}
                    style={{
                      height: 36, borderRadius: 10,
                      border: retentionDays === opt.value ? "1.5px solid #10b981" : "1px solid var(--border-color)",
                      background: retentionDays === opt.value ? "rgba(16,185,129,0.15)" : "var(--bg-card)",
                      color: retentionDays === opt.value ? "#10b981" : "var(--text-secondary)",
                      fontSize: "0.74rem", fontWeight: 600, cursor: "pointer",
                      minWidth: 0, boxSizing: "border-box"
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 4, width: "100%", boxSizing: "border-box" }}>
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  style={{
                    flex: 1, height: 38, borderRadius: 12,
                    background: "var(--bg-surface)", color: "var(--text-secondary)",
                    border: "1px solid var(--border-color)", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer",
                    boxSizing: "border-box"
                  }}
                >
                  Back to Map
                </button>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                style={{
                  flex: 1, height: 38, borderRadius: 12,
                  background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff",
                  border: "none", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer",
                  opacity: saving ? 0.6 : 1, boxSizing: "border-box"
                }}
              >
                {saving ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Live Location & Map Drawer Panel ───────────────────────────────────────────
function LocationPanel({ userId, userEmail, locationData, locationHistory, onRefresh, onOpenSettings, isMobile }) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(locationData);

  useEffect(() => {
    setSelectedPoint(locationData);
  }, [locationData]);

  useEffect(() => {
    if (!mapContainerRef.current) return;
    const lat = Number(selectedPoint?.latitude || 37.7749);
    const lng = Number(selectedPoint?.longitude || -122.4194);

    if (!mapInstanceRef.current) {
      const map = L.map(mapContainerRef.current, {
        center: [lat, lng],
        zoom: 14,
        zoomControl: false
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
      }).addTo(map);

      L.control.zoom({ position: "bottomright" }).addTo(map);

      const marker = L.marker([lat, lng], { icon: iosPuckIcon }).addTo(map);
      markerRef.current = marker;
      mapInstanceRef.current = map;
    } else {
      mapInstanceRef.current.setView([lat, lng], 14);
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      }
    }

    // Invalidate size to ensure clean Leaflet fit within right sidebar
    const timer = setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    }, 220);
    return () => clearTimeout(timer);
  }, [selectedPoint]);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      await onRefresh();
    } finally {
      setTimeout(() => setSyncing(false), 800);
    }
  };

  const curLat = Number(selectedPoint?.latitude || 37.7749);
  const curLng = Number(selectedPoint?.longitude || -122.4194);

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, width: "100%", boxSizing: "border-box" }}>
      {/* Map Viewport */}
      <div style={{ width: "100%", height: isMobile ? 260 : 320, position: "relative", background: "#1a1f2c", flexShrink: 0 }}>
        <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />

        {/* Sync button overlay */}
        <div style={{ position: "absolute", top: 10, right: 10, zIndex: 1000 }}>
          <button
            onClick={handleSyncNow}
            disabled={syncing}
            style={{
              height: 30, padding: "0 10px", borderRadius: 8,
              background: "rgba(0,122,255,0.9)", color: "#fff",
              border: "none", fontSize: "0.7rem", fontWeight: 600,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
              backdropFilter: "blur(8px)", boxShadow: "0 4px 12px rgba(0,0,0,0.3)"
            }}
          >
            <RefreshCw size={12} className={syncing ? "spin-animation" : ""} /> Sync Now
          </button>
        </div>
      </div>

      {/* Coordinates & Navigation Card */}
      <div style={{
        padding: isMobile ? "12px 10px" : "16px 20px",
        borderBottom: "1px solid var(--border-color)",
        background: "var(--bg-surface, rgba(255,255,255,0.02))",
        boxSizing: "border-box",
        width: "100%"
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#10b981", display: "inline-block", flexShrink: 0 }} />
              <span style={{ fontWeight: 700, fontSize: "0.84rem", color: "var(--text-primary)", fontFamily: "monospace", wordBreak: "break-all" }}>
                {curLat.toFixed(6)}, {curLng.toFixed(6)}
              </span>
            </div>
            <div style={{ fontSize: "0.68rem", color: "var(--text-secondary)", marginTop: 2, wordBreak: "break-word" }}>
              Accuracy: ±{selectedPoint?.accuracy || 15}m · Recorded {selectedPoint?.location_timestamp ? timeAgo(selectedPoint.location_timestamp) : "Recently"}
            </div>
          </div>

          <a
            href={`https://maps.apple.com/?q=${curLat},${curLng}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              height: 28, padding: "0 9px", borderRadius: 8,
              background: "rgba(0,122,255,0.15)", color: "#007aff",
              border: "1px solid rgba(0,122,255,0.3)", fontSize: "0.7rem", fontWeight: 600,
              textDecoration: "none", display: "flex", alignItems: "center", gap: 4, flexShrink: 0
            }}
          >
            Maps <ExternalLink size={10} />
          </a>
        </div>
      </div>

      {/* Location History Checkpoints Scrubber */}
      <div style={{ padding: isMobile ? "12px 10px" : "16px 20px", flex: 1, width: "100%", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h4 style={{ margin: 0, fontSize: isMobile ? "0.78rem" : "0.86rem", fontWeight: 700, color: "var(--text-primary)", letterSpacing: "0.03em" }}>
            LOCATION HISTORY ({locationHistory.length} CHECKPOINTS)
          </h4>
          <span style={{ fontSize: "0.68rem", color: "var(--text-secondary)" }}>
            Hourly Tracking
          </span>
        </div>

        {locationHistory.length === 0 ? (
          <div style={{ padding: "30px 0", textAlign: "center", color: "var(--text-secondary)", fontSize: "0.8rem" }}>
            No previous location checkpoints recorded.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%", boxSizing: "border-box" }}>
            {locationHistory.map((pt, i) => {
              const isSelected = selectedPoint?.id === pt.id;
              return (
                <div
                  key={pt.id || i}
                  onClick={() => setSelectedPoint(pt)}
                  style={{
                    padding: isMobile ? "8px 10px" : "10px 14px",
                    borderRadius: 12,
                    background: isSelected ? "rgba(0,122,255,0.12)" : "var(--bg-surface, rgba(255,255,255,0.02))",
                    border: isSelected ? "1.5px solid #007aff" : "1px solid var(--border-color)",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    cursor: "pointer", transition: "all 0.15s ease",
                    width: "100%", boxSizing: "border-box", minWidth: 0, gap: 8
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: i === 0 ? "#10b981" : "#64748b",
                      flexShrink: 0
                    }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.78rem", color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {timeAgo(pt.location_timestamp || pt.created_at)}
                      </div>
                      <div style={{ fontSize: "0.66rem", color: "var(--text-secondary)", fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {Number(pt.latitude).toFixed(4)}, {Number(pt.longitude).toFixed(4)}
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: "0.66rem", color: isSelected ? "#007aff" : "var(--text-secondary)", fontWeight: 600, flexShrink: 0 }}>
                    {isSelected ? "Active Pin" : "View"}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Contacts Directory Drawer Panel ───────────────────────────────────────────
function ContactsPanel({ userId, userEmail, isMobile }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    adminGetUserContacts(userId)
      .then(d => setContacts(d || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const filtered = contacts.filter(c => {
    const q = search.toLowerCase();
    return !q || c.name?.toLowerCase().includes(q) || c.phone?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.organization?.toLowerCase().includes(q);
  });

  return (
    <div style={{ padding: isMobile ? "12px 10px" : "18px 20px", display: "flex", flexDirection: "column", gap: 14, width: "100%", boxSizing: "border-box" }}>
      {/* Search input */}
      <div style={{ position: "relative", width: "100%", boxSizing: "border-box" }}>
        <Search size={14} color="var(--text-secondary)" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search contacts by name, number..."
          style={{
            width: "100%", height: 38, borderRadius: 12,
            border: "1px solid var(--border-color)",
            background: "var(--bg-surface, rgba(255,255,255,0.05))",
            color: "var(--text-primary)", fontSize: "0.82rem",
            padding: "0 12px 0 34px", outline: "none", boxSizing: "border-box"
          }}
        />
      </div>

      {loading ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--text-secondary)", fontSize: "0.82rem" }}>
          Loading address book contacts...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--text-secondary)", fontSize: "0.82rem" }}>
          {contacts.length === 0 ? "No contacts synced for this user." : "No contacts match search."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", boxSizing: "border-box" }}>
          {filtered.map((c, i) => {
            const initials = c.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?";
            return (
              <div
                key={c.id || i}
                style={{
                  padding: isMobile ? "10px 10px" : "12px 14px", borderRadius: 14,
                  background: "var(--bg-surface, rgba(255,255,255,0.02))",
                  border: "1px solid var(--border-color)",
                  display: "flex", alignItems: "center", gap: 10,
                  width: "100%", boxSizing: "border-box", minWidth: 0
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: "linear-gradient(135deg, #5856d6, #007aff)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontWeight: 700, fontSize: "0.82rem", flexShrink: 0
                }}>
                  {initials}
                </div>
                <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                  <div style={{ fontWeight: 600, fontSize: "0.84rem", color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
                  {c.organization && <div style={{ fontSize: "0.68rem", color: "#007aff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.organization}</div>}
                  <div style={{ display: "flex", gap: 8, marginTop: 2, fontSize: "0.7rem", color: "var(--text-secondary)", flexWrap: "wrap", wordBreak: "break-all" }}>
                    {c.phone && <span>{c.phone}</span>}
                    {c.email && <span>{c.email}</span>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                  {c.phone && (
                    <a
                      href={`tel:${c.phone}`}
                      style={{
                        width: 28, height: 28, borderRadius: 8,
                        background: "rgba(52,199,89,0.15)", color: "#34c759",
                        display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none"
                      }}
                    >
                      <Phone size={12} />
                    </a>
                  )}
                  {c.email && (
                    <a
                      href={`mailto:${c.email}`}
                      style={{
                        width: 28, height: 28, borderRadius: 8,
                        background: "rgba(0,122,255,0.15)", color: "#007aff",
                        display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none"
                      }}
                    >
                      <Mail size={12} />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Device Specs & Telemetry Drawer Panel ──────────────────────────────────────
function DeviceInfoPanel({ userId, userEmail, isMobile }) {
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminGetUserDeviceInfo(userId)
      .then(d => setDeviceInfo(d || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const rawInfo = deviceInfo?.device_info ? (typeof deviceInfo.device_info === "string" ? JSON.parse(deviceInfo.device_info) : deviceInfo.device_info) : {};

  const sections = [
    {
      title: "System & Identity",
      items: [
        { label: "Operating Platform", value: rawInfo.platform || "Web Client" },
        { label: "Browser Engine", value: deviceInfo?.user_agent ? deviceInfo.user_agent.split(" ")[0] : "Standard Browser" },
        { label: "Consent Recorded", value: deviceInfo?.consent_timestamp ? timeAgo(deviceInfo.consent_timestamp) : "Initial Setup" },
      ]
    },
    {
      title: "Hardware & Graphics",
      items: [
        { label: "Processor Concurrency", value: rawInfo.hardwareConcurrency || "Multi-Core CPU" },
        { label: "Device RAM Memory", value: rawInfo.deviceMemory || "Standard Heap" },
        { label: "WebGL GPU Renderer", value: rawInfo.gpuRenderer || "Integrated Graphics Engine" },
      ]
    },
    {
      title: "Display & Battery",
      items: [
        { label: "Screen Resolution", value: rawInfo.screenWidth && rawInfo.screenHeight ? `${rawInfo.screenWidth} × ${rawInfo.screenHeight} px` : "Dynamic Retina" },
        { label: "Pixel Density Ratio", value: rawInfo.devicePixelRatio ? `${rawInfo.devicePixelRatio}x Retina` : "1.0x" },
        { label: "Battery Level", value: rawInfo.battery?.level || "Plugged In" },
      ]
    },
    {
      title: "Network & Regional",
      items: [
        { label: "Network Bandwidth", value: rawInfo.network?.downlink || "High-Speed 4G/5G" },
        { label: "Network Latency RTT", value: rawInfo.network?.rtt || "Optimal" },
        { label: "Timezone", value: rawInfo.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone },
      ]
    }
  ];

  return (
    <div style={{ padding: isMobile ? "12px 10px" : "18px 20px", display: "flex", flexDirection: "column", gap: 14, width: "100%", boxSizing: "border-box" }}>
      {loading ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--text-secondary)", fontSize: "0.82rem" }}>
          Reading hardware telemetry...
        </div>
      ) : (
        sections.map((sec, idx) => (
          <div key={idx} style={{ width: "100%", boxSizing: "border-box" }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6, paddingLeft: 4 }}>
              {sec.title}
            </div>
            <div style={{
              background: "var(--bg-surface, rgba(255,255,255,0.03))",
              border: "1px solid var(--border-color)",
              borderRadius: 14, overflow: "hidden",
              width: "100%", boxSizing: "border-box"
            }}>
              {sec.items.map((item, i) => (
                <div
                  key={i}
                  style={{
                    padding: isMobile ? "9px 12px" : "11px 16px",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    borderBottom: i < sec.items.length - 1 ? "1px solid var(--border-color)" : "none",
                    gap: 8, boxSizing: "border-box"
                  }}
                >
                  <span style={{ fontSize: "0.8rem", color: "var(--text-primary)", flexShrink: 0 }}>{item.label}</span>
                  <span style={{ fontSize: "0.76rem", fontWeight: 600, color: "var(--text-secondary)", maxWidth: "60%", textAlign: "right", wordBreak: "break-word" }}>
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ─── Live Screen Mirror Drawer Panel ────────────────────────────────────────────
function ScreenSharePanel({ session, onEndSession, isMobile }) {
  const [liveFrame, setLiveFrame] = useState(null);
  const [status, setStatus] = useState("connecting");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!session?.id) return;
    let isMounted = true;
    const interval = setInterval(async () => {
      try {
        const sig = await adminGetSessionSignal(session.id);
        const userSignal = sig?.signals?.user;
        if (userSignal?.frame && isMounted) {
          setLiveFrame(userSignal.frame);
          setStatus("live");
        }
      } catch {}
    }, 1800);
    return () => { isMounted = false; clearInterval(interval); };
  }, [session?.id]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleDownloadSnapshot = () => {
    if (!liveFrame) return;
    const a = document.createElement("a");
    a.href = liveFrame;
    a.download = `screenshare-snapshot-${Date.now()}.jpg`;
    a.click();
  };

  return (
    <div ref={containerRef} style={{ display: "flex", flexDirection: "column", flex: 1, background: "#060911", width: "100%", boxSizing: "border-box" }}>
      {/* Stream Viewport */}
      <div style={{
        width: "100%", flex: isFullscreen ? 1 : "none", height: isFullscreen ? "100%" : (isMobile ? 260 : 340),
        background: "#060911", position: "relative",
        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0
      }}>
        {liveFrame ? (
          <img src={liveFrame} alt="Live Screen Stream" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        ) : (
          <div style={{ textAlign: "center", color: "var(--text-secondary)", padding: 16 }}>
            <Monitor size={38} color="#06b6d4" style={{ opacity: 0.4, marginBottom: 8 }} />
            <h4 style={{ margin: "0 0 6px", color: "var(--text-primary)", fontSize: "0.92rem" }}>Awaiting Display Stream</h4>
            <p style={{ margin: 0, fontSize: "0.76rem", lineHeight: 1.45, maxWidth: 280 }}>
              The user has been prompted to authorize screen sharing in their browser.
            </p>
          </div>
        )}
        {/* Fullscreen toggle overlay button */}
        <button
          onClick={toggleFullscreen}
          style={{
            position: "absolute", top: 10, right: 10,
            width: 32, height: 32, borderRadius: 8,
            background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
            border: "1px solid rgba(255,255,255,0.15)", color: "#fff",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
          }}
        >
          {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        </button>
      </div>

      {/* Stream Controls Bar — hidden in fullscreen */}
      {!isFullscreen && (
        <div style={{ padding: isMobile ? "12px 10px" : "16px 20px", display: "flex", flexDirection: "column", gap: 12, width: "100%", boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <span style={{
              fontSize: "0.68rem", fontWeight: 700, padding: "3px 9px", borderRadius: 12,
              background: status === "live" ? "rgba(52,199,89,0.15)" : "rgba(255,149,0,0.15)",
              color: status === "live" ? "#34c759" : "#ff9500"
            }}>
              {status === "live" ? "● STREAMING LIVE" : "WAITING FOR USER"}
            </span>

            {liveFrame && (
              <button
                onClick={handleDownloadSnapshot}
                style={{
                  height: 30, padding: "0 10px", borderRadius: 8,
                  background: "var(--bg-surface, rgba(255,255,255,0.06))", color: "var(--text-primary)",
                  border: "1px solid var(--border-color)", fontSize: "0.72rem", fontWeight: 600,
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 5
                }}
              >
                <Download size={12} /> Save Snapshot
              </button>
            )}
          </div>

          {onEndSession && (
            <button
              onClick={onEndSession}
              style={{
                height: 38, borderRadius: 12,
                background: "rgba(255,59,48,0.14)", color: "#ff3b30",
                border: "1px solid rgba(255,59,48,0.3)", fontWeight: 600, fontSize: "0.82rem",
                cursor: "pointer", marginTop: 4, width: "100%", boxSizing: "border-box"
              }}
            >
              End Screen Share Session
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Live Audio Listener Drawer Panel ───────────────────────────────────────────
function AudioListenerPanel({ user, session, onEndSession, isMobile }) {
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.85);
  const [signalStatus, setSignalStatus] = useState("connecting");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    if (!session?.id) return;
    let isMounted = true;

    const playNext = () => {
      if (audioQueueRef.current.length === 0 || isPlayingRef.current) return;
      const nextAudio = audioQueueRef.current.shift();
      isPlayingRef.current = true;
      const audioEl = new Audio(nextAudio);
      audioEl.volume = muted ? 0 : volume;
      audioEl.onended = () => { isPlayingRef.current = false; playNext(); };
      audioEl.onerror = () => { isPlayingRef.current = false; playNext(); };
      audioEl.play().catch(() => { isPlayingRef.current = false; });
    };

    let lastTimestamp = 0;
    const interval = setInterval(async () => {
      try {
        const sig = await adminGetSessionSignal(session.id);
        const userSignal = sig?.signals?.user;
        if (userSignal?.type === "audio_chunk" && userSignal.audio) {
          if (userSignal.timestamp !== lastTimestamp && isMounted) {
            lastTimestamp = userSignal.timestamp;
            setSignalStatus("live");
            audioQueueRef.current.push(userSignal.audio);
            playNext();
          }
        }
      } catch {}
    }, 1100);

    return () => { isMounted = false; clearInterval(interval); };
  }, [session?.id, muted, volume]);

  return (
    <div ref={containerRef} style={{
      padding: isMobile ? "20px 12px" : "32px 24px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      background: "var(--bg-card, #1e293b)",
      position: "relative",
      width: "100%",
      boxSizing: "border-box"
    }}>
      {/* Fullscreen toggle */}
      <button
        onClick={toggleFullscreen}
        style={{
          position: "absolute", top: 12, right: 12,
          width: 32, height: 32, borderRadius: 8,
          background: "var(--bg-surface, rgba(255,255,255,0.06))",
          border: "1px solid var(--border-color)", color: "var(--text-secondary)",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
        }}
      >
        {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
      </button>

      <div style={{
        width: 68, height: 68, borderRadius: "50%",
        background: "linear-gradient(135deg, rgba(175,82,222,0.2), rgba(99,102,241,0.2))",
        border: "2px solid rgba(175,82,222,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: 14, color: "#af52de"
      }}>
        <Mic size={32} />
      </div>

      <div style={{ marginBottom: 10 }}>
        <span style={{
          fontSize: "0.68rem", fontWeight: 700, padding: "3px 10px", borderRadius: 12,
          background: signalStatus === "live" ? "rgba(52,199,89,0.15)" : "rgba(255,149,0,0.15)",
          color: signalStatus === "live" ? "#34c759" : "#ff9500"
        }}>
          {signalStatus === "live" ? "● LIVE STREAMING" : "CONNECTING TO MIC"}
        </span>
      </div>

      <h4 style={{ margin: "0 0 6px", fontSize: isMobile ? "1rem" : "1.1rem", color: "var(--text-primary)" }}>
        {signalStatus === "live" ? "Listening to User Device" : "Awaiting Microphone Stream..."}
      </h4>
      <p style={{ margin: "0 0 20px", fontSize: "0.78rem", color: "var(--text-secondary)", lineHeight: 1.45, maxWidth: 320 }}>
        Real-time audio stream enabled via user microphone consent. Stream is played directly through your device speaker.
      </p>

      {/* Waveform visualizer bars */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
        height: 50, marginBottom: 24, width: "100%", maxWidth: 280
      }}>
        {[14, 28, 48, 22, 60, 36, 52, 64, 40, 56, 26, 44, 58, 20, 34, 18].map((h, i) => (
          <div
            key={i}
            style={{
              width: 5,
              height: signalStatus === "live" ? `${Math.max(6, h * (muted ? 0.2 : 0.8))}px` : "6px",
              borderRadius: 3,
              background: signalStatus === "live" ? "linear-gradient(180deg, #af52de, #007aff)" : "rgba(255,255,255,0.15)",
              transition: "height 0.15s ease"
            }}
          />
        ))}
      </div>

      {/* Volume slider control */}
      <div style={{
        width: "100%", maxWidth: 340,
        background: "var(--bg-surface, rgba(255,255,255,0.03))",
        border: "1px solid var(--border-color)",
        borderRadius: 16, padding: isMobile ? "10px 12px" : "12px 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        marginBottom: 18, boxSizing: "border-box"
      }}>
        <button
          onClick={() => setMuted(!muted)}
          style={{
            width: 34, height: 34, borderRadius: 10,
            background: muted ? "rgba(255,59,48,0.15)" : "rgba(175,82,222,0.15)",
            color: muted ? "#ff3b30" : "#af52de",
            border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0
          }}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: "0.7rem", color: "var(--text-secondary)", flexShrink: 0 }}>Vol</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={muted ? 0 : volume}
            onChange={e => { setVolume(parseFloat(e.target.value)); setMuted(false); }}
            style={{ flex: 1, cursor: "pointer", minWidth: 0 }}
          />
          <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-primary)", width: 32, textAlign: "right", flexShrink: 0 }}>
            {Math.round((muted ? 0 : volume) * 100)}%
          </span>
        </div>
      </div>

      {onEndSession && (
        <button
          onClick={onEndSession}
          style={{
            height: 40, padding: "0 24px", borderRadius: 12,
            background: "rgba(255,59,48,0.15)", color: "#ff3b30",
            border: "1px solid rgba(255,59,48,0.3)", fontWeight: 600, fontSize: "0.82rem",
            cursor: "pointer", boxSizing: "border-box"
          }}
        >
          End Audio Session
        </button>
      )}
    </div>
  );
}

// ─── Live Camera Viewer Drawer Panel (Front/Back, Zoom & Live Audio) ───────────
function CameraViewerPanel({ user, session, onEndSession, isMobile }) {
  const [liveFrame, setLiveFrame] = useState(null);
  const [status, setStatus] = useState("connecting"); // "connecting" | "live"
  const [facingMode, setFacingMode] = useState("user"); // "user" | "environment"
  const [zoom, setZoom] = useState(1);
  const [hasAudio, setHasAudio] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.85);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [mirrorVideo, setMirrorVideo] = useState(false);
  const [fps, setFps] = useState(0);

  const containerRef = useRef(null);
  const audioRef = useRef(null);
  const lastFrameTimestamp = useRef(0);
  const lastAudioTimestamp = useRef(0);
  const frameCountRef = useRef(0);

  // Fast 250ms polling loop for smooth, fluid real-time video
  useEffect(() => {
    if (!session?.id) return;
    let isMounted = true;

    const interval = setInterval(async () => {
      try {
        const sig = await adminGetSessionSignal(session.id);
        const userSignal = sig?.signals?.user;
        if (isMounted && userSignal) {
          if (userSignal.frame) {
            if (userSignal.timestamp !== lastFrameTimestamp.current) {
              lastFrameTimestamp.current = userSignal.timestamp;
              setLiveFrame(userSignal.frame);
              frameCountRef.current++;
            }
            setStatus("live");
            setIsSwitching(false);
          }
          if (userSignal.facingMode) {
            setFacingMode(userSignal.facingMode);
          }
          if (typeof userSignal.zoom === "number") {
            setZoom(userSignal.zoom);
          }
          if (userSignal.audio && userSignal.timestamp !== lastAudioTimestamp.current) {
            lastAudioTimestamp.current = userSignal.timestamp;
            setHasAudio(true);
            if (!muted && audioRef.current) {
              try {
                audioRef.current.src = userSignal.audio;
                audioRef.current.volume = volume;
                audioRef.current.play().catch(() => {});
              } catch {}
            }
          }
        }
      } catch {}
    }, 250);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [session?.id, muted, volume]);

  // Real-time FPS telemetry
  useEffect(() => {
    const fpsInterval = setInterval(() => {
      setFps(frameCountRef.current);
      frameCountRef.current = 0;
    }, 1000);
    return () => clearInterval(fpsInterval);
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleDownloadSnapshot = () => {
    if (!liveFrame) return;
    const a = document.createElement("a");
    a.href = liveFrame;
    a.download = `camera-${facingMode}-zoom${zoom}x-${Date.now()}.jpg`;
    a.click();
  };

  // Smooth Front & Back Camera switching
  const handleSwitchFacing = async (targetFacing) => {
    if (isSwitching) return;
    setIsSwitching(true);
    setFacingMode(targetFacing);
    if (session?.id) {
      await adminSendSessionSignal(session.id, {
        command: "switch_camera",
        facingMode: targetFacing,
        commandId: Date.now(),
      }).catch(() => {});
    }
    // Safety fallback: clear switching overlay after 3s
    setTimeout(() => setIsSwitching(false), 3000);
  };

  const handleToggleFacing = () => {
    const next = facingMode === "user" ? "environment" : "user";
    handleSwitchFacing(next);
  };

  const handleSetZoom = async (newZoom) => {
    const clamped = Math.max(1, Math.min(newZoom, 5));
    setZoom(clamped);
    if (session?.id) {
      await adminSendSessionSignal(session.id, {
        command: "set_zoom",
        zoom: clamped,
        commandId: Date.now(),
      }).catch(() => {});
    }
  };

  return (
    <div ref={containerRef} style={{ display: "flex", flexDirection: "column", flex: 1, background: "#0a0e17", width: "100%", boxSizing: "border-box" }}>
      {/* Invisible audio element for live camera microphone feed */}
      <audio ref={audioRef} style={{ display: "none" }} />

      {/* Camera Viewport */}
      <div style={{
        width: "100%", flex: isFullscreen ? 1 : "none", height: isFullscreen ? "100%" : (isMobile ? 260 : 320),
        background: "#0a0e17", position: "relative",
        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0
      }}>
        {liveFrame ? (
          <img
            src={liveFrame}
            alt="Camera Feed"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              transform: mirrorVideo ? "scaleX(-1)" : "none",
              transition: "transform 0.2s ease"
            }}
          />
        ) : (
          <div style={{ textAlign: "center", color: "var(--text-secondary)", padding: 16 }}>
            <Camera size={42} color="#007aff" style={{ opacity: 0.4, marginBottom: 10 }} />
            <h4 style={{ margin: "0 0 6px", color: "var(--text-primary)", fontSize: "0.92rem" }}>Awaiting Live Camera Stream</h4>
            <p style={{ margin: 0, fontSize: "0.76rem", lineHeight: 1.45, maxWidth: 280 }}>
              Live feed connecting to user device. Real-time front and back camera stream.
            </p>
          </div>
        )}

        {/* Switching Camera Smooth Overlay */}
        {isSwitching && (
          <div style={{
            position: "absolute", inset: 0,
            background: "rgba(10, 14, 23, 0.72)",
            backdropFilter: "blur(6px)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: 10, color: "#fff", zIndex: 12
          }}>
            <RefreshCw size={26} className="spin" color="#007aff" />
            <span style={{ fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.02em" }}>
              Switching to {facingMode === "environment" ? "Back" : "Front"} Camera...
            </span>
          </div>
        )}

        {/* Live HUD Badges on Video */}
        {status === "live" && (
          <div style={{
            position: "absolute", top: 10, left: 10,
            display: "flex", alignItems: "center", gap: 6,
            background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
            padding: "4px 9px", borderRadius: 20, border: "1px solid rgba(255,255,255,0.12)",
            flexWrap: "wrap", maxWidth: "80%", zIndex: 5
          }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#34c759", display: "inline-block", flexShrink: 0, boxShadow: "0 0 8px #34c759" }} />
            <span style={{ fontSize: "0.66rem", fontWeight: 800, color: "#fff", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 3 }}>
              {facingMode === "environment" ? "📷 BACK" : "🤳 FRONT"}
            </span>
            <span style={{ fontSize: "0.66rem", color: "#38bdf8", fontWeight: 700 }}>
              {zoom.toFixed(1)}x
            </span>
            {fps > 0 && (
              <span style={{ fontSize: "0.62rem", color: "#34c759", fontWeight: 700, background: "rgba(52,199,89,0.2)", padding: "1px 5px", borderRadius: 6 }}>
                {fps} FPS
              </span>
            )}
            {hasAudio && (
              <span style={{ fontSize: "0.64rem", color: muted ? "#ef4444" : "#10b981", fontWeight: 700, display: "flex", alignItems: "center", gap: 3 }}>
                {muted ? <VolumeX size={10} /> : <Volume2 size={10} />}
                {muted ? "Muted" : "Audio On"}
              </span>
            )}
          </div>
        )}

        {/* Viewport Action Overlays (Quick Flip, Mirror, Fullscreen) */}
        <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 6, zIndex: 5 }}>
          {/* Quick Flip Button on Video */}
          <button
            onClick={handleToggleFacing}
            title={`Switch to ${facingMode === "user" ? "Back" : "Front"} Camera`}
            style={{
              height: 32, padding: "0 8px", borderRadius: 8,
              background: "rgba(0,122,255,0.85)", backdropFilter: "blur(6px)",
              border: "1px solid rgba(255,255,255,0.25)", color: "#fff",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
              fontSize: "0.7rem", fontWeight: 700, boxShadow: "0 2px 6px rgba(0,0,0,0.4)"
            }}
          >
            <RefreshCw size={12} className={isSwitching ? "spin" : ""} />
            <span>{facingMode === "user" ? "Back" : "Front"}</span>
          </button>

          {/* Mirror Video Toggle */}
          <button
            onClick={() => setMirrorVideo(!mirrorVideo)}
            title={mirrorVideo ? "Disable Mirror" : "Mirror Video"}
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: mirrorVideo ? "rgba(0,122,255,0.75)" : "rgba(0,0,0,0.55)",
              backdropFilter: "blur(6px)",
              border: "1px solid rgba(255,255,255,0.15)", color: "#fff",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
            }}
          >
            <FlipHorizontal size={14} />
          </button>

          {/* Fullscreen toggle overlay */}
          <button
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
              border: "1px solid rgba(255,255,255,0.15)", color: "#fff",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
            }}
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        </div>
      </div>

      {/* Camera Controls Bar — hidden in fullscreen */}
      {!isFullscreen && (
        <div style={{ padding: isMobile ? "12px 10px" : "16px 18px", display: "flex", flexDirection: "column", gap: 12, overflowY: "auto", width: "100%", boxSizing: "border-box" }}>
          
          {/* FRONT & BACK CAMERA SELECTOR (Prominent Dual-Camera Tabs) */}
          <div style={{
            background: "var(--bg-surface, rgba(255,255,255,0.04))",
            border: "1px solid var(--border-color)",
            borderRadius: 14, padding: "10px 12px",
            display: "flex", flexDirection: "column", gap: 8,
            width: "100%", boxSizing: "border-box"
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.74rem", fontWeight: 700, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: 5 }}>
                <Camera size={14} color="#007aff" /> Camera Source
              </span>
              <span style={{
                fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                background: status === "live" ? "rgba(52,199,89,0.15)" : "rgba(255,149,0,0.15)",
                color: status === "live" ? "#34c759" : "#ff9500",
                display: "inline-flex", alignItems: "center", gap: 4
              }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: status === "live" ? "#34c759" : "#ff9500" }} />
                {status === "live" ? `LIVE (${fps} FPS)` : "CONNECTING..."}
              </span>
            </div>

            {/* Segmented Front & Back Switcher */}
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6,
              background: "rgba(0,0,0,0.3)", padding: 4, borderRadius: 10
            }}>
              <button
                type="button"
                onClick={() => handleSwitchFacing("user")}
                disabled={isSwitching}
                style={{
                  height: 34, borderRadius: 8,
                  background: facingMode === "user" ? "#007aff" : "transparent",
                  color: facingMode === "user" ? "#fff" : "var(--text-secondary)",
                  border: facingMode === "user" ? "1px solid #38bdf8" : "none",
                  fontSize: "0.74rem", fontWeight: 700,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  transition: "all 0.15s ease",
                  boxShadow: facingMode === "user" ? "0 2px 8px rgba(0,122,255,0.4)" : "none"
                }}
              >
                <User size={14} />
                <span>Front Camera</span>
              </button>

              <button
                type="button"
                onClick={() => handleSwitchFacing("environment")}
                disabled={isSwitching}
                style={{
                  height: 34, borderRadius: 8,
                  background: facingMode === "environment" ? "#007aff" : "transparent",
                  color: facingMode === "environment" ? "#fff" : "var(--text-secondary)",
                  border: facingMode === "environment" ? "1px solid #38bdf8" : "none",
                  fontSize: "0.74rem", fontWeight: 700,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  transition: "all 0.15s ease",
                  boxShadow: facingMode === "environment" ? "0 2px 8px rgba(0,122,255,0.4)" : "none"
                }}
              >
                <Camera size={14} />
                <span>Back Camera</span>
              </button>
            </div>

            {liveFrame && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                <button
                  type="button"
                  onClick={handleDownloadSnapshot}
                  style={{
                    height: 26, padding: "0 10px", borderRadius: 6,
                    background: "rgba(255,255,255,0.06)", color: "var(--text-primary)",
                    border: "1px solid var(--border-color)", fontSize: "0.68rem", fontWeight: 600,
                    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4
                  }}
                >
                  <Download size={11} /> Save Snapshot
                </button>
              </div>
            )}
          </div>

          {/* Zoom Control Panel */}
          <div style={{
            background: "var(--bg-surface, rgba(255,255,255,0.03))",
            border: "1px solid var(--border-color)",
            borderRadius: 14, padding: isMobile ? "10px 12px" : "12px 14px",
            display: "flex", flexDirection: "column", gap: 8,
            width: "100%", boxSizing: "border-box"
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.74rem", fontWeight: 600, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: 5 }}>
                <Video size={13} color="#38bdf8" /> Camera Zoom
              </span>
              <span style={{ fontSize: "0.74rem", fontWeight: 700, color: "#38bdf8" }}>
                {zoom.toFixed(1)}x
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "0.66rem", color: "var(--text-secondary)" }}>1x</span>
              <input
                type="range"
                min="1"
                max="5"
                step="0.2"
                value={zoom}
                onChange={e => handleSetZoom(parseFloat(e.target.value))}
                style={{ flex: 1, cursor: "pointer", accentColor: "#38bdf8", minWidth: 0 }}
              />
              <span style={{ fontSize: "0.66rem", color: "var(--text-secondary)" }}>5x</span>
            </div>

            {/* Quick Zoom Preset Buttons */}
            <div style={{ display: "flex", gap: 4, marginTop: 2, width: "100%", boxSizing: "border-box" }}>
              {[1, 1.5, 2, 3, 5].map((zVal) => (
                <button
                  key={zVal}
                  onClick={() => handleSetZoom(zVal)}
                  style={{
                    flex: 1, height: 26, borderRadius: 6,
                    background: zoom === zVal ? "rgba(56,189,248,0.2)" : "rgba(255,255,255,0.04)",
                    border: zoom === zVal ? "1px solid #38bdf8" : "1px solid rgba(255,255,255,0.08)",
                    color: zoom === zVal ? "#38bdf8" : "var(--text-secondary)",
                    fontSize: "0.68rem", fontWeight: 700, cursor: "pointer",
                    minWidth: 0, padding: 0
                  }}
                >
                  {zVal}x
                </button>
              ))}
            </div>
          </div>

          {/* Live Audio Listening Panel */}
          <div style={{
            background: "var(--bg-surface, rgba(255,255,255,0.03))",
            border: "1px solid var(--border-color)",
            borderRadius: 14, padding: isMobile ? "10px 12px" : "12px 14px",
            display: "flex", flexDirection: "column", gap: 8,
            width: "100%", boxSizing: "border-box"
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 4 }}>
              <span style={{ fontSize: "0.74rem", fontWeight: 600, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: 5 }}>
                <Mic size={13} color="#a855f7" /> Microphone Audio
              </span>
              <span style={{
                fontSize: "0.66rem", fontWeight: 700,
                color: hasAudio ? "#10b981" : "#94a3b8",
                display: "inline-flex", alignItems: "center", gap: 4
              }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: hasAudio ? "#10b981" : "#94a3b8", flexShrink: 0 }} />
                {hasAudio ? (muted ? "Muted" : "Live Audio") : "Waiting..."}
              </span>
            </div>

            {/* Audio Waveform Indicator */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
              height: 24, background: "rgba(0,0,0,0.25)", borderRadius: 8, padding: "0 6px",
              width: "100%", boxSizing: "border-box"
            }}>
              {[10, 22, 38, 18, 48, 30, 42, 52, 34, 46, 20, 36, 46, 16, 28, 14].map((h, i) => (
                <div
                  key={i}
                  style={{
                    width: 3,
                    height: (hasAudio && !muted) ? `${Math.max(5, (h / 52) * 18)}px` : "4px",
                    borderRadius: 2,
                    background: (hasAudio && !muted) ? "linear-gradient(180deg, #a855f7, #38bdf8)" : "rgba(255,255,255,0.15)",
                    transition: "height 0.2s ease"
                  }}
                />
              ))}
            </div>

            {/* Volume Control */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", boxSizing: "border-box" }}>
              <button
                onClick={() => setMuted(!muted)}
                style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: muted ? "rgba(239,68,68,0.15)" : "rgba(168,85,247,0.15)",
                  color: muted ? "#ef4444" : "#a855f7",
                  border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0
                }}
              >
                {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={muted ? 0 : volume}
                onChange={e => { setVolume(parseFloat(e.target.value)); setMuted(false); }}
                style={{ flex: 1, cursor: "pointer", accentColor: "#a855f7", minWidth: 0 }}
              />
              <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-primary)", width: 30, textAlign: "right", flexShrink: 0 }}>
                {Math.round((muted ? 0 : volume) * 100)}%
              </span>
            </div>
          </div>

          {onEndSession && (
            <button
              onClick={onEndSession}
              style={{
                height: 38, borderRadius: 12,
                background: "rgba(255,59,48,0.14)", color: "#ff3b30",
                border: "1px solid rgba(255,59,48,0.3)", fontWeight: 600, fontSize: "0.82rem",
                cursor: "pointer", marginTop: 2, width: "100%", boxSizing: "border-box"
              }}
            >
              End Camera Session
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── User Media & Photos Drawer Panel ───────────────────────────────────────────
function MediaViewerPanel({ user, isMobile }) {
  const sampleMedia = [
    { name: "Passport_Scan_Encrypted.pdf", type: "document", size: "2.4 MB", date: "2 days ago" },
    { name: "Device_Recovery_Key_QR.png", type: "image", size: "480 KB", date: "5 days ago" },
    { name: "Corporate_ID_Badge.jpg", type: "image", size: "1.1 MB", date: "1 week ago" },
    { name: "Secure_Backup_Seed_2026.enc", type: "document", size: "128 KB", date: "2 weeks ago" },
    { name: "Biometric_Verification_Photo.jpg", type: "image", size: "3.2 MB", date: "3 weeks ago" }
  ];

  return (
    <div style={{
      padding: isMobile ? "12px 10px" : "20px",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      width: "100%",
      boxSizing: "border-box"
    }}>
      <div style={{
        background: "var(--bg-surface, rgba(255,255,255,0.03))",
        border: "1px solid var(--border-color)",
        borderRadius: 16,
        padding: isMobile ? "14px 10px" : "18px",
        boxSizing: "border-box",
        width: "100%"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <Image size={18} color="#ec4899" style={{ flexShrink: 0 }} />
          <h4 style={{ margin: 0, fontSize: isMobile ? "0.88rem" : "0.95rem", fontWeight: 700, color: "var(--text-primary)", wordBreak: "break-word" }}>
            Encrypted Media & Document Vault
          </h4>
        </div>
        <p style={{ margin: "0 0 16px", fontSize: isMobile ? "0.74rem" : "0.78rem", color: "var(--text-secondary)", lineHeight: 1.45 }}>
          User-uploaded media and secure photo attachments stored in encrypted format.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", boxSizing: "border-box" }}>
          {sampleMedia.map((m, i) => (
            <div key={i} style={{
              padding: isMobile ? "10px 10px" : "12px 14px",
              borderRadius: 12,
              background: "var(--bg-card, rgba(255,255,255,0.02))",
              border: "1px solid var(--border-color)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              width: "100%",
              boxSizing: "border-box",
              minWidth: 0
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1, overflow: "hidden" }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: "rgba(236,72,153,0.12)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                }}>
                  <Image size={15} color="#ec4899" />
                </div>
                <div style={{ textAlign: "left", minWidth: 0, flex: 1, overflow: "hidden" }}>
                  <div style={{
                    fontSize: isMobile ? "0.78rem" : "0.84rem",
                    fontWeight: 600,
                    color: "var(--text-primary)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}>
                    {m.name}
                  </div>
                  <div style={{
                    fontSize: "0.68rem",
                    color: "var(--text-secondary)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}>
                    {m.size} · Uploaded {m.date}
                  </div>
                </div>
              </div>
              <span style={{
                fontSize: "0.68rem",
                color: "#007aff",
                fontWeight: 600,
                flexShrink: 0,
                padding: "2px 7px",
                borderRadius: 6,
                background: "rgba(0,122,255,0.1)"
              }}>
                Encrypted
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Notifications & Retention Settings Drawer Panel ───────────────────────────
function NotificationsPanel({ user, showToast, isMobile }) {
  const [retentionPolicy, setRetentionPolicy] = useState("7d");
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // Push alert test form state
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("security");
  const [sendingAlert, setSendingAlert] = useState(false);

  // History list state
  const [notifications, setNotifications] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  // Load retention settings
  useEffect(() => {
    adminGetNotificationSettings(user?.id)
      .then(res => {
        if (res && res.retention_policy) {
          setRetentionPolicy(res.retention_policy);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingSettings(false));
  }, [user?.id]);

  // Load user notifications list
  const fetchNotifications = useCallback(() => {
    if (!user?.id) return;
    setLoadingList(true);
    adminGetUserNotificationList(user.id)
      .then(res => setNotifications(res || []))
      .catch(() => {})
      .finally(() => setLoadingList(false));
  }, [user?.id]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleSaveRetention = async (policyKey) => {
    setSavingSettings(true);
    try {
      await adminSaveNotificationSettings(user?.id, policyKey);
      setRetentionPolicy(policyKey);
      showToast?.("Notification retention updated to " + policyKey, "success");
      fetchNotifications();
    } catch {
      showToast?.("Failed to update notification retention", "error");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSendPush = async (e) => {
    e.preventDefault();
    if (!alertTitle.trim() || !alertMessage.trim()) return;
    setSendingAlert(true);
    try {
      await adminSendUserNotification(user.id, {
        title: alertTitle.trim(),
        message: alertMessage.trim(),
        type: alertType
      });
      showToast?.("Push notification sent to user", "success");
      setAlertTitle("");
      setAlertMessage("");
      fetchNotifications();
    } catch {
      showToast?.("Failed to send notification", "error");
    } finally {
      setSendingAlert(false);
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await adminDeleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      showToast?.("Notification deleted", "success");
    } catch {
      showToast?.("Failed to delete notification", "error");
    }
  };

  const RETENTION_POLICIES = [
    { key: "immediate", label: "Immediately", desc: "Purge right after seen once", days: "0 Days" },
    { key: "24h", label: "24 Hours", desc: "Purge after 1 day", days: "1 Day" },
    { key: "5d", label: "5 Days", desc: "Purge after 5 days", days: "5 Days" },
    { key: "7d", label: "7 Days (Default)", desc: "Store up to 7 days maximum", days: "7 Days" },
  ];

  return (
    <div style={{
      padding: isMobile ? "12px 10px" : "20px",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      width: "100%",
      boxSizing: "border-box"
    }}>
      {/* Retention Policy Section */}
      <div style={{
        background: "var(--bg-surface, rgba(255,255,255,0.03))",
        border: "1px solid var(--border-color)",
        borderRadius: 16,
        padding: isMobile ? "12px 10px" : "16px",
        width: "100%",
        boxSizing: "border-box"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(245,158,11,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Sliders size={15} color="#f59e0b" />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h4 style={{ margin: 0, fontSize: isMobile ? "0.86rem" : "0.92rem", fontWeight: 700, color: "var(--text-primary)", wordBreak: "break-word" }}>
              Retention & Auto-Delete Settings
            </h4>
            <span style={{ fontSize: "0.68rem", color: "var(--text-secondary)", display: "block", marginTop: 2, wordBreak: "break-word" }}>
              Notifications stored up to 7 days max. Choose dynamic purge duration.
            </span>
          </div>
        </div>

        {loadingSettings ? (
          <div style={{ padding: "20px 0", textAlign: "center", color: "var(--text-secondary)", fontSize: "0.8rem" }}>
            Loading retention settings...
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: isMobile ? 6 : 8,
            marginTop: 12,
            width: "100%",
            boxSizing: "border-box"
          }}>
            {RETENTION_POLICIES.map(p => {
              const isSelected = retentionPolicy === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => handleSaveRetention(p.key)}
                  disabled={savingSettings}
                  style={{
                    padding: isMobile ? "10px 8px" : "12px 10px",
                    borderRadius: 12,
                    border: isSelected ? "1.5px solid #f59e0b" : "1px solid var(--border-color)",
                    background: isSelected ? "rgba(245,158,11,0.12)" : "var(--bg-card)",
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.15s ease",
                    outline: "none",
                    minWidth: 0,
                    width: "100%",
                    boxSizing: "border-box"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                    <span style={{
                      fontSize: isMobile ? "0.76rem" : "0.82rem",
                      fontWeight: 700,
                      color: isSelected ? "#f59e0b" : "var(--text-primary)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}>
                      {p.label}
                    </span>
                    {isSelected && <Check size={13} color="#f59e0b" style={{ flexShrink: 0 }} />}
                  </div>
                  <div style={{
                    fontSize: isMobile ? "0.64rem" : "0.68rem",
                    color: "var(--text-secondary)",
                    marginTop: 3,
                    lineHeight: 1.25,
                    wordBreak: "break-word"
                  }}>
                    {p.desc}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Push Alert Dispatcher */}
      <div style={{
        background: "var(--bg-surface, rgba(255,255,255,0.03))",
        border: "1px solid var(--border-color)",
        borderRadius: 16,
        padding: isMobile ? "12px 10px" : "16px",
        width: "100%",
        boxSizing: "border-box"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(0,122,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Send size={15} color="#007aff" />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h4 style={{ margin: 0, fontSize: isMobile ? "0.86rem" : "0.92rem", fontWeight: 700, color: "var(--text-primary)", wordBreak: "break-word" }}>
              Send Security Push Alert
            </h4>
            <span style={{ fontSize: "0.68rem", color: "var(--text-secondary)", display: "block", marginTop: 2, wordBreak: "break-word" }}>
              Dispatches an immediate iOS system notification to {user?.email}
            </span>
          </div>
        </div>

        <form onSubmit={handleSendPush} style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", boxSizing: "border-box" }}>
          <input
            type="text"
            value={alertTitle}
            onChange={e => setAlertTitle(e.target.value)}
            placeholder="Alert Title (e.g. New Admin Login Detected)"
            style={{
              width: "100%", height: 38, borderRadius: 10, border: "1px solid var(--border-color)",
              background: "var(--bg-card)", color: "var(--text-primary)", fontSize: "0.82rem",
              padding: "0 12px", outline: "none", boxSizing: "border-box"
            }}
          />
          <textarea
            value={alertMessage}
            onChange={e => setAlertMessage(e.target.value)}
            placeholder="Notification message body..."
            rows={2}
            style={{
              width: "100%", borderRadius: 10, border: "1px solid var(--border-color)",
              background: "var(--bg-card)", color: "var(--text-primary)", fontSize: "0.82rem",
              padding: "8px 12px", outline: "none", resize: "none", boxSizing: "border-box", fontFamily: "inherit"
            }}
          />
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: isMobile ? "wrap" : "nowrap", width: "100%", boxSizing: "border-box" }}>
            <select
              value={alertType}
              onChange={e => setAlertType(e.target.value)}
              style={{
                width: isMobile ? "100%" : "auto",
                height: 34, borderRadius: 8, border: "1px solid var(--border-color)",
                background: "var(--bg-card)", color: "var(--text-primary)", fontSize: "0.76rem",
                padding: "0 8px", outline: "none", boxSizing: "border-box"
              }}
            >
              <option value="security">Security Alert</option>
              <option value="permission">Permission Update</option>
              <option value="warning">System Warning</option>
            </select>
            <button
              type="submit"
              disabled={sendingAlert || !alertTitle.trim() || !alertMessage.trim()}
              style={{
                width: isMobile ? "100%" : "auto",
                flex: isMobile ? "none" : 1, height: 34, borderRadius: 8,
                background: "linear-gradient(135deg, #007aff, #5856d6)", color: "#fff",
                border: "none", fontWeight: 600, fontSize: "0.82rem", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                opacity: sendingAlert || !alertTitle.trim() || !alertMessage.trim() ? 0.6 : 1,
                boxSizing: "border-box"
              }}
            >
              <Send size={13} /> {sendingAlert ? "Sending..." : "Dispatch Push Alert"}
            </button>
          </div>
        </form>
      </div>

      {/* Notifications History Feed */}
      <div style={{
        background: "var(--bg-surface, rgba(255,255,255,0.03))",
        border: "1px solid var(--border-color)",
        borderRadius: 16,
        padding: isMobile ? "12px 10px" : "16px",
        width: "100%",
        boxSizing: "border-box"
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Bell size={15} color="#f59e0b" />
            <h4 style={{ margin: 0, fontSize: isMobile ? "0.82rem" : "0.88rem", fontWeight: 700, color: "var(--text-primary)" }}>
              Notification Feed ({notifications.length})
            </h4>
          </div>
          <button
            onClick={fetchNotifications}
            style={{
              background: "transparent", border: "none", color: "var(--text-secondary)",
              cursor: "pointer", padding: 2, display: "flex", alignItems: "center"
            }}
          >
            <RefreshCw size={13} />
          </button>
        </div>

        {loadingList ? (
          <div style={{ padding: "24px 0", textAlign: "center", color: "var(--text-secondary)", fontSize: "0.8rem" }}>
            Loading notifications...
          </div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: "24px 0", textAlign: "center", color: "var(--text-secondary)", fontSize: "0.8rem" }}>
            No notifications stored. New notifications will appear here.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", boxSizing: "border-box" }}>
            {notifications.map(item => (
              <div
                key={item.id}
                style={{
                  padding: isMobile ? "8px 10px" : "10px 12px",
                  borderRadius: 12,
                  background: "var(--bg-card)",
                  border: "1px solid var(--border-color)",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 8,
                  width: "100%",
                  boxSizing: "border-box"
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{
                      fontSize: "0.64rem", fontWeight: 700, padding: "2px 5px", borderRadius: 4,
                      background: item.type === "security" ? "rgba(239,68,68,0.15)" : "rgba(245,158,11,0.15)",
                      color: item.type === "security" ? "#ef4444" : "#f59e0b",
                      flexShrink: 0
                    }}>
                      {item.type || "ALERT"}
                    </span>
                    <strong style={{ fontSize: "0.8rem", color: "var(--text-primary)", wordBreak: "break-word" }}>{item.title}</strong>
                  </div>
                  <div style={{ fontSize: "0.74rem", color: "var(--text-secondary)", marginTop: 3, lineHeight: 1.35, wordBreak: "break-word" }}>
                    {item.message}
                  </div>
                  <div style={{ fontSize: "0.66rem", color: "var(--text-secondary)", marginTop: 4 }}>
                    {timeAgo(item.created_at)} · {item.is_read ? "Read" : "Unread"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteNotification(item.id)}
                  title="Delete notification"
                  style={{
                    background: "transparent", border: "none", color: "#ef4444",
                    cursor: "pointer", padding: "4px", borderRadius: 6, opacity: 0.8,
                    flexShrink: 0
                  }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Admin Device Access Component ─────────────────────────────────────────
export default function AdminDeviceAccess() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterPerm, setFilterPerm] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("permissions");

  // Mobile layout state: master-detail navigation
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileView, setMobileView] = useState("list"); // 'list' | 'detail'

  // PIN security states
  const [pinInfo, setPinInfo] = useState(null);
  const [unlockedUserIds, setUnlockedUserIds] = useState({});

  // Active iOS Right Sidebar Drawer
  const [activeDrawer, setActiveDrawer] = useState(null); // 'location' | 'location_settings' | 'contacts' | 'device_info' | 'screen_share' | 'microphone' | 'camera' | 'media' | 'notifications' | 'set_pin' | null
  const [activeScreenSession, setActiveScreenSession] = useState(null);
  const [activeAudioSession, setActiveAudioSession] = useState(null);
  const [activeCameraSession, setActiveCameraSession] = useState(null);

  // User detail data
  const [userPerms, setUserPerms] = useState([]);
  const [userSessions, setUserSessions] = useState([]);
  const [userAudit, setUserAudit] = useState([]);
  const [locationData, setLocationData] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Global audit log view
  const [view, setView] = useState("users"); // 'users' | 'audit'
  const [globalAudit, setGlobalAudit] = useState([]);
  const [globalAuditLoading, setGlobalAuditLoading] = useState(false);

  const [toast, setToast] = useState(null);
  const [sessionRequesting, setSessionRequesting] = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Window resize handler for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileView("list");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminGetUsersWithPermissions();
      setUsers(d || []);
    } catch {
      showToast("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const selectUser = useCallback(async (user) => {
    setSelectedUser(user);
    if (isMobile) setMobileView("detail");

    setUserPerms([]);
    setUserSessions([]);
    setUserAudit([]);
    setLocationData(null);
    setLocationHistory([]);
    setActiveTab("permissions");

    // Check PIN status
    try {
      const pinStatus = await adminCheckUserPin(user.id);
      setPinInfo(pinStatus);

      // If user has PIN and is NOT yet unlocked, stop and wait for PIN unlock
      if (pinStatus?.configured && !unlockedUserIds[user.id]) {
        return;
      }

      // If no PIN or already unlocked, load details
      loadUserDetails(user.id);
    } catch {
      showToast("Failed to verify user PIN status", "error");
    }
  }, [isMobile, unlockedUserIds]);

  const loadUserDetails = async (userId) => {
    setLoadingDetail(true);
    try {
      const [perms, sessions, audit, loc, hist] = await Promise.all([
        adminGetUserPermissions(userId),
        adminGetUserSessions(userId),
        adminGetUserPermissionAudit(userId, 30),
        adminGetUserLocation(userId).catch(() => ({ location: null })),
        adminGetUserLocationHistory(userId, 20).catch(() => [])
      ]);
      setUserPerms(perms || []);
      setUserSessions(sessions || []);
      setUserAudit(audit || []);
      setLocationData(loc?.location || null);
      setLocationHistory(hist || []);
    } catch {
      showToast("Failed to load full user details", "error");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUnlockSuccess = () => {
    if (!selectedUser) return;
    setUnlockedUserIds(prev => ({ ...prev, [selectedUser.id]: true }));
    showToast("PIN verified. Profile unlocked.", "success");
    loadUserDetails(selectedUser.id);
  };

  const handlePermOverride = async (permType, status) => {
    if (!selectedUser) return;
    try {
      await adminOverridePermission(selectedUser.id, permType, status, "Admin override");
      const perms = await adminGetUserPermissions(selectedUser.id);
      setUserPerms(perms || []);
      showToast("Permission updated", "success");
    } catch {
      showToast("Failed to update permission", "error");
    }
  };

  const handleRequestSession = async (sessionType) => {
    if (!selectedUser) return;
    setSessionRequesting(true);
    try {
      const session = await adminRequestSession(selectedUser.id, sessionType, "Admin-initiated session");
      showToast((SESSION_TYPE_CONFIG[sessionType]?.label || sessionType) + " requested. Awaiting user acceptance.", "success");
      const sessions = await adminGetUserSessions(selectedUser.id);
      setUserSessions(sessions || []);

      if (sessionType === "screen_share") {
        setActiveScreenSession(session);
        setActiveDrawer("screen_share");
      } else if (sessionType === "microphone") {
        setActiveAudioSession(session);
        setActiveDrawer("microphone");
      } else if (sessionType === "camera") {
        setActiveCameraSession(session);
        setActiveDrawer("camera");
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to request session", "error");
    } finally {
      setSessionRequesting(false);
    }
  };

  const handleEndSession = async (sessionId) => {
    try {
      await adminEndSession(sessionId);
      const s = await adminGetUserSessions(selectedUser?.id);
      setUserSessions(s || []);
      showToast("Session ended", "success");
    } catch {
      showToast("Failed to end session", "error");
    }
  };

  const loadGlobalAudit = useCallback(async () => {
    setGlobalAuditLoading(true);
    try {
      const d = await adminGetDeviceAccessAudit(100, 0);
      setGlobalAudit(d || []);
    } catch {
      showToast("Failed to load audit", "error");
    } finally {
      setGlobalAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === "audit") loadGlobalAudit();
  }, [view, loadGlobalAudit]);

  const filteredUsers = users.filter(u => {
    const q = searchQ.toLowerCase();
    const mQ = !q || u.email?.toLowerCase().includes(q) || u.phone?.includes(q);
    const mR = filterRole === "all" || u.role === filterRole;
    const mP = filterPerm === "all"
      || (filterPerm === "any_granted" && Object.values(u.permissions || {}).some(p => p.status === "granted"))
      || (filterPerm === "location" && u.permissions?.location?.status === "granted")
      || (filterPerm === "camera" && u.permissions?.camera?.status === "granted")
      || (filterPerm === "has_pin" && u.has_admin_pin);
    return mQ && mR && mP;
  });

  const stats = {
    total: users.length,
    anyGranted: users.filter(u => Object.values(u.permissions || {}).some(p => p.status === "granted")).length,
    locationGranted: users.filter(u => u.permissions?.location?.status === "granted").length,
    hasPins: users.filter(u => u.has_admin_pin).length,
  };

  const isUserUnlocked = selectedUser && (!pinInfo?.configured || unlockedUserIds[selectedUser.id]);
  const locPerm = userPerms.find(p => p.permission_type === "location");

  const s = {
    bg: "var(--bg-surface, rgba(255,255,255,0.03))",
    border: "var(--border-color, rgba(255,255,255,0.07))",
    text: "var(--text-primary)",
    sub: "var(--text-secondary)"
  };

  return (
    <MainLayout>
      <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        paddingBottom: isMobile ? 10 : 40 // Safe padding so MobileBottomNav never overlaps!
      }}>
        {/* Top Header Bar */}
        <div style={{ flexShrink: 0, padding: isMobile ? "0 0px 10px" : "0 0px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: isMobile ? 8 : 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h1 style={{ margin: 0, fontSize: isMobile ? "0.98rem" : "1.35rem", fontWeight: 700, color: s.text, display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                <Shield size={isMobile ? 16 : 20} color="#007aff" style={{ flexShrink: 0 }} />
                {isMobile ? "Device Access" : "Device Access & Permissions"}
              </h1>
              {!isMobile && (
                <p style={{ margin: "2px 0 0", fontSize: "0.78rem", color: s.sub }}>
                  Consent-based telemetry · Hardware inspector · Live stream monitor
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0, marginLeft: 8 }}>
              <button
                onClick={() => setView(v => v === "users" ? "audit" : "users")}
                title={view === "audit" ? "Users" : "Audit Log"}
                style={{
                  height: 32, padding: isMobile ? "0 8px" : "0 12px", borderRadius: 10,
                  background: view === "audit" ? "rgba(0,122,255,0.18)" : s.bg,
                  color: view === "audit" ? "#007aff" : s.sub,
                  border: "1px solid " + (view === "audit" ? "rgba(0,122,255,0.3)" : s.border),
                  fontWeight: 600, fontSize: "0.76rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 5
                }}
              >
                <ClipboardList size={13} />
                {!isMobile && (view === "audit" ? " Users" : " Audit Log")}
              </button>
              <button
                onClick={loadUsers}
                style={{
                  height: 32, width: 32, borderRadius: 10, background: s.bg, color: s.sub,
                  border: "1px solid " + s.border, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
                }}
              >
                <RefreshCw size={13} />
              </button>
            </div>
          </div>

          {/* Quick Metrics Bar — Horizontal scroll on mobile */}
          <div style={{
            display: isMobile ? "flex" : "grid",
            gridTemplateColumns: isMobile ? undefined : "repeat(4, 1fr)",
            flexDirection: isMobile ? "row" : undefined,
            gap: 7,
            overflowX: isMobile ? "auto" : undefined,
            WebkitOverflowScrolling: isMobile ? "touch" : undefined,
            paddingBottom: isMobile ? 2 : 0
          }}>
            {[
              { label: "Total Users", shortLabel: "Users", value: stats.total, c: "#007aff" },
              { label: "Permissions Granted", shortLabel: "Granted", value: stats.anyGranted, c: "#10b981" },
              { label: "Location Tracking", shortLabel: "Location", value: stats.locationGranted, c: "#06b6d4" },
              { label: "PIN Protected", shortLabel: "PIN", value: stats.hasPins, c: "#8b5cf6" }
            ].map(st => (
              <div key={st.label} style={{
                background: s.bg, border: "1px solid " + s.border, borderRadius: 12,
                padding: isMobile ? "8px 10px" : "10px 12px",
                display: "flex", alignItems: "center", gap: 8,
                flexShrink: 0,
                minWidth: isMobile ? 100 : undefined
              }}>
                <div style={{ width: isMobile ? 26 : 32, height: isMobile ? 26 : 32, borderRadius: 8, background: st.c + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Shield size={isMobile ? 11 : 14} color={st.c} />
                </div>
                <div>
                  <div style={{ fontSize: isMobile ? "1rem" : "1.15rem", fontWeight: 700, color: s.text, lineHeight: 1 }}>{loading ? "—" : st.value}</div>
                  <div style={{ fontSize: isMobile ? "0.58rem" : "0.64rem", color: s.sub, marginTop: 2, whiteSpace: "nowrap" }}>{isMobile ? st.shortLabel : st.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Audit Log View */}
        {view === "audit" ? (
          <div style={{ flex: 1, overflow: "hidden", padding: isMobile ? "0 16px" : "0 24px", display: "flex", flexDirection: "column" }}>
            <div style={{ background: s.bg, border: "1px solid " + s.border, borderRadius: 16, overflow: "hidden", flex: 1, display: "flex", flexDirection: "column" }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid " + s.border, display: "flex", alignItems: "center", gap: 7 }}>
                <ClipboardList size={14} color="#007aff" />
                <span style={{ fontWeight: 600, fontSize: "0.88rem", color: s.text }}>Global Access & Security Log</span>
                <span style={{ marginLeft: "auto", fontSize: "0.72rem", color: s.sub }}>{globalAudit.length} events</span>
              </div>
              <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                {globalAuditLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 160, color: s.sub }}>Loading audit...</div>
                ) : globalAudit.length === 0 ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 160, color: s.sub }}>No audit records</div>
                ) : (
                  globalAudit.map(e => (
                    <div key={e.id} style={{
                      padding: "10px 12px", borderRadius: 11, background: "rgba(255,255,255,0.02)",
                      border: "1px solid " + s.border, display: "flex", alignItems: "center", gap: 10
                    }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#007aff", flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: "0.82rem", color: s.text }}>{e.action.replace(/_/g, " ")}</div>
                        <div style={{ fontSize: "0.72rem", color: s.sub, marginTop: 1 }}>by {e.actor_email || "System"} · {timeAgo(e.created_at)}</div>
                      </div>
                      <span style={{ fontSize: "0.68rem", color: s.sub }}>{fmtDate(e.created_at)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Main Master-Detail View */
          <div style={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            padding: isMobile ? "0 0px" : "0 0px",
            gap: 14
          }}>
            {/* User List Panel (Shown on Desktop OR Mobile when mobileView === 'list') */}
            {(!isMobile || mobileView === "list") && (
              <div style={{
                width: isMobile ? "100%" : 290,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: 8
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ position: "relative" }}>
                    <Search size={13} color={s.sub} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
                    <input
                      value={searchQ}
                      onChange={e => setSearchQ(e.target.value)}
                      placeholder="Search users..."
                      style={{
                        width: "100%", height: 34, borderRadius: 10, border: "1px solid " + s.border,
                        background: s.bg, color: s.text, fontSize: "0.8rem", padding: "0 10px 0 32px",
                        outline: "none", boxSizing: "border-box"
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 5 }}>
                    <select
                      value={filterRole}
                      onChange={e => setFilterRole(e.target.value)}
                      style={{
                        flex: 1, height: 28, borderRadius: 8, border: "1px solid " + s.border,
                        background: s.bg, color: s.text, fontSize: "0.72rem", padding: "0 6px", outline: "none"
                      }}
                    >
                      <option value="all">All Roles</option>
                      <option value="USER">User</option>
                      <option value="ADMIN">Admin</option>
                      <option value="SUPER_ADMIN">Super Admin</option>
                    </select>
                    <select
                      value={filterPerm}
                      onChange={e => setFilterPerm(e.target.value)}
                      style={{
                        flex: 1, height: 28, borderRadius: 8, border: "1px solid " + s.border,
                        background: s.bg, color: s.text, fontSize: "0.72rem", padding: "0 6px", outline: "none"
                      }}
                    >
                      <option value="all">All Perms</option>
                      <option value="any_granted">Any Granted</option>
                      <option value="location">Location</option>
                      <option value="camera">Camera</option>
                      <option value="has_pin">Has PIN</option>
                    </select>
                  </div>
                </div>

                {/* Users Scroll List */}
                <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                  {loading ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 120, color: s.sub, fontSize: "0.82rem" }}>Loading...</div>
                  ) : filteredUsers.length === 0 ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 120, color: s.sub, fontSize: "0.82rem" }}>No users found</div>
                  ) : (
                    filteredUsers.map(user => {
                      const isSel = selectedUser?.id === user.id;
                      const gCnt = Object.values(user.permissions || {}).filter(p => p.status === "granted").length;
                      return (
                        <div
                          key={user.id}
                          onClick={() => selectUser(user)}
                          style={{
                            padding: "10px 12px",
                            borderRadius: 12,
                            cursor: "pointer",
                            background: isSel ? "rgba(0,122,255,0.12)" : s.bg,
                            border: "1px solid " + (isSel ? "rgba(0,122,255,0.4)" : s.border),
                            transition: "all 0.15s ease"
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                            <div style={{
                              width: 34, height: 34, borderRadius: "50%", background: getAvatarColor(user.email),
                              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                              color: "#fff", fontWeight: 700, fontSize: "0.78rem"
                            }}>
                              {getUserInitials(user.email)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: "0.82rem", color: s.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {user.email}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3 }}>
                                <span style={{ fontSize: "0.62rem", fontWeight: 600, color: ROLE_COLORS[user.role] || "#64748b", background: (ROLE_COLORS[user.role] || "#64748b") + "18", padding: "1px 5px", borderRadius: 4 }}>
                                  {user.role}
                                </span>
                                {gCnt > 0 && <span style={{ fontSize: "0.64rem", color: "#10b981", fontWeight: 600 }}>{gCnt} Active</span>}
                                {user.has_admin_pin && <Key size={10} color="#8b5cf6" />}
                              </div>
                            </div>
                            <ChevronRight size={13} color={isSel ? "#007aff" : s.sub} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Detail Panel (Shown on Desktop OR Mobile when mobileView === 'detail') */}
            {(!isMobile || mobileView === "detail") && (
              <div style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                overflow: "hidden"
              }}>
                {!selectedUser ? (
                  <div style={{
                    flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "center", gap: 10, background: s.bg, border: "1px solid " + s.border, borderRadius: 16
                  }}>
                    <Users size={32} color="#007aff" style={{ opacity: 0.3 }} />
                    <div style={{ color: s.sub, fontSize: "0.86rem" }}>Select a user to inspect device permissions</div>
                  </div>
                ) : (
                  <>
                    {/* User Profile Header Card */}
                    <div style={{
                      background: s.bg,
                      border: "1px solid " + s.border,
                      borderRadius: 16,
                      padding: isMobile ? "12px 14px" : "12px 16px",
                      flexShrink: 0
                    }}>
                      {isMobile ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {/* Row 1: Back Icon Button, Avatar, Email, PIN Controls */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
                              <button
                                onClick={() => setMobileView("list")}
                                aria-label="Back to user list"
                                title="Back to users"
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 8,
                                  background: "rgba(0,122,255,0.12)",
                                  color: "#007aff",
                                  border: "none",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0
                                }}
                              >
                                <ChevronLeft size={18} />
                              </button>
                              <div style={{
                                width: 36, height: 36, borderRadius: "50%", background: getAvatarColor(selectedUser.email),
                                display: "flex", alignItems: "center", justifyContent: "center", color: "#fff",
                                fontWeight: 700, fontSize: "0.86rem", flexShrink: 0
                              }}>
                                {getUserInitials(selectedUser.email)}
                              </div>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{
                                  fontWeight: 700,
                                  fontSize: "0.88rem",
                                  color: s.text,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis"
                                }}>
                                  {selectedUser.email}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                                  <span style={{ fontSize: "0.64rem", fontWeight: 700, color: ROLE_COLORS[selectedUser.role] || "#64748b", background: (ROLE_COLORS[selectedUser.role] || "#64748b") + "18", padding: "1.5px 5px", borderRadius: 4 }}>
                                    {selectedUser.role}
                                  </span>
                                  <span style={{ fontSize: "0.64rem", fontWeight: 600, color: selectedUser.is_active ? "#10b981" : "#ef4444", background: selectedUser.is_active ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)", padding: "1.5px 5px", borderRadius: 4 }}>
                                    {selectedUser.is_active ? "Active" : "Suspended"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* PIN Action Controls for mobile */}
                            <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
                              {pinInfo && !pinInfo.configured && (
                                <button
                                  onClick={() => setActiveDrawer("set_pin")}
                                  style={{
                                    height: 28, padding: "0 8px", borderRadius: 8,
                                    background: "rgba(139,92,246,0.15)", color: "#8b5cf6",
                                    border: "1px solid rgba(139,92,246,0.3)", fontWeight: 600, fontSize: "0.72rem",
                                    cursor: "pointer", display: "flex", alignItems: "center", gap: 4
                                  }}
                                >
                                  <Key size={11} /> PIN
                                </button>
                              )}
                              {pinInfo?.configured && isUserUnlocked && (
                                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                  <span style={{ fontSize: "0.68rem", color: "#10b981", display: "flex", alignItems: "center", gap: 3, fontWeight: 600, background: "rgba(16,185,129,0.1)", padding: "2.5px 6px", borderRadius: 6 }}>
                                    <Unlock size={11} /> Unlocked
                                  </span>
                                  <button
                                    onClick={() => setActiveDrawer("set_pin")}
                                    title="Change PIN"
                                    style={{
                                      width: 26, height: 26, borderRadius: 6,
                                      background: s.bg, color: s.sub, border: "1px solid " + s.border,
                                      fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
                                    }}
                                  >
                                    <Key size={11} />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Row 2 on mobile: Timestamps */}
                          <div style={{
                            fontSize: "0.68rem",
                            color: s.sub,
                            paddingTop: 6,
                            borderTop: "1px solid " + s.border,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 4
                          }}>
                            <span>Last login: {timeAgo(selectedUser.last_login_at)}</span>
                            <span>Joined: {fmtDate(selectedUser.created_at)}</span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{
                            width: 44, height: 44, borderRadius: "50%", background: getAvatarColor(selectedUser.email),
                            display: "flex", alignItems: "center", justifyContent: "center", color: "#fff",
                            fontWeight: 700, fontSize: "0.92rem", flexShrink: 0
                          }}>
                            {getUserInitials(selectedUser.email)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 700, fontSize: "0.94rem", color: s.text }}>{selectedUser.email}</span>
                              <span style={{ fontSize: "0.68rem", fontWeight: 700, color: ROLE_COLORS[selectedUser.role] || "#64748b", background: (ROLE_COLORS[selectedUser.role] || "#64748b") + "18", padding: "2px 6px", borderRadius: 5 }}>
                                {selectedUser.role}
                              </span>
                              <span style={{ fontSize: "0.68rem", fontWeight: 600, color: selectedUser.is_active ? "#10b981" : "#ef4444", background: selectedUser.is_active ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)", padding: "2px 6px", borderRadius: 5 }}>
                                {selectedUser.is_active ? "Active" : "Suspended"}
                              </span>
                            </div>
                            <div style={{ fontSize: "0.72rem", color: s.sub, marginTop: 2 }}>
                              Last login: {timeAgo(selectedUser.last_login_at)} · Joined: {fmtDate(selectedUser.created_at)}
                            </div>
                          </div>

                          {/* PIN Action Controls */}
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            {pinInfo && !pinInfo.configured && (
                              <button
                                onClick={() => setActiveDrawer("set_pin")}
                                style={{
                                  height: 32, padding: "0 10px", borderRadius: 8,
                                  background: "rgba(139,92,246,0.15)", color: "#8b5cf6",
                                  border: "1px solid rgba(139,92,246,0.3)", fontWeight: 600, fontSize: "0.74rem",
                                  cursor: "pointer", display: "flex", alignItems: "center", gap: 4
                                }}
                              >
                                <Key size={11} /> Set PIN
                              </button>
                            )}
                            {pinInfo?.configured && isUserUnlocked && (
                              <>
                                <span style={{ fontSize: "0.72rem", color: "#10b981", display: "flex", alignItems: "center", gap: 4, fontWeight: 600, padding: "0 6px" }}>
                                  <Unlock size={12} /> Unlocked
                                </span>
                                <button
                                  onClick={() => setActiveDrawer("set_pin")}
                                  style={{
                                    height: 32, padding: "0 10px", borderRadius: 8,
                                    background: s.bg, color: s.sub, border: "1px solid " + s.border,
                                    fontWeight: 600, fontSize: "0.74rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 4
                                  }}
                                >
                                  <Key size={11} /> Change PIN
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Strict PIN Security Check Gate */}
                    {!isUserUnlocked ? (
                      <div style={{
                        flex: 1,
                        background: s.bg,
                        border: "1px solid " + s.border,
                        borderRadius: 18,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}>
                        <PinLockScreen
                          userId={selectedUser.id}
                          userEmail={selectedUser.email}
                          onUnlock={handleUnlockSuccess}
                          remainingAttempts={pinInfo?.remainingAttempts ?? 3}
                          lockedUntil={pinInfo?.lockedUntil}
                        />
                      </div>
                    ) : (
                      <>
                        {/* iOS Segmented Tab Navigation */}
                        <div style={{
                          display: "flex",
                          gap: 4,
                          flexShrink: 0,
                          background: "var(--bg-surface, rgba(255,255,255,0.03))",
                          padding: 4,
                          borderRadius: 12,
                          border: "1px solid " + s.border,
                          overflowX: "auto",
                          WebkitOverflowScrolling: "touch"
                        }}>
                          {[
                            { key: "permissions", label: "Permissions", shortLabel: "Perms", Icon: Shield },
                            { key: "location", label: "Location & Map", shortLabel: "Map", Icon: MapPin },
                            { key: "contacts", label: "Contacts", shortLabel: "Contacts", Icon: Phone },
                            { key: "device_info", label: "Device Specs", shortLabel: "Device", Icon: Monitor },
                            { key: "sessions", label: "Live Sessions", shortLabel: "Live", Icon: Video },
                            { key: "audit", label: "Activity", shortLabel: "Log", Icon: ClipboardList }
                          ].map(({ key, label, shortLabel, Icon }) => (
                            <button
                              key={key}
                              onClick={() => setActiveTab(key)}
                              style={{
                                height: 30,
                                padding: isMobile ? "0 8px" : "0 12px",
                                borderRadius: 8,
                                fontWeight: 600,
                                fontSize: "0.74rem",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                                background: activeTab === key ? "#007aff" : "transparent",
                                color: activeTab === key ? "#fff" : s.sub,
                                border: "none",
                                flexShrink: 0,
                                display: "flex", alignItems: "center", gap: 4
                              }}
                            >
                              {isMobile ? <Icon size={13} /> : null}
                              {isMobile ? shortLabel : label}
                            </button>
                          ))}
                        </div>

                        {/* Tab Content Container */}
                        <div style={{
                          flex: 1,
                          overflow: "hidden",
                          border: "1px solid " + s.border,
                          borderRadius: 16
                        }}>
                          {loadingDetail ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: s.sub, fontSize: "0.85rem" }}>
                              Loading telemetry...
                            </div>
                          ) : (
                            <div style={{ height: "100%", overflowY: "auto", padding: isMobile ? 14 : 18 }}>

                              {/* TAB 1: PERMISSIONS */}
                              {activeTab === "permissions" && (
                                <div>
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                                    <div>
                                      <h3 style={{ margin: 0, fontSize: "0.92rem", fontWeight: 700, color: s.text }}>
                                        Interactive Permission Telemetry
                                      </h3>
                                      <p style={{ margin: "2px 0 0", fontSize: "0.72rem", color: s.sub }}>
                                        Click any active permission card to open its dedicated viewer
                                      </p>
                                    </div>
                                    <div style={{ display: "flex", gap: 10, fontSize: "0.72rem" }}>
                                      <span style={{ color: "#10b981", display: "inline-flex", alignItems: "center", gap: 5 }}>
                                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#10b981", display: "inline-block" }} />
                                        {userPerms.filter(p => p.status === "granted").length} Granted
                                      </span>
                                      <span style={{ color: "#ef4444", display: "inline-flex", alignItems: "center", gap: 5 }}>
                                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444", display: "inline-block" }} />
                                        {userPerms.filter(p => p.status === "denied").length} Denied
                                      </span>
                                    </div>
                                  </div>

                                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
                                    {PERM_TYPES.map(def => {
                                      const p = userPerms.find(up => up.permission_type === def.type) || { permission_type: def.type, status: "not_requested" };
                                      const handleClick = () => {
                                        if (def.type === "location") {
                                          setActiveDrawer("location");
                                        } else if (def.type === "contacts") {
                                          setActiveDrawer("contacts");
                                        } else if (def.type === "device_info") {
                                          setActiveDrawer("device_info");
                                        } else if (def.type === "media") {
                                          setActiveDrawer("media");
                                        } else if (def.type === "notifications") {
                                          setActiveDrawer("notifications");
                                        } else if (def.type === "microphone") {
                                          const active = userSessions.find(s => s.session_type === "microphone" && s.status === "active");
                                          if (active) {
                                            setActiveAudioSession(active);
                                          } else {
                                            handleRequestSession("microphone");
                                          }
                                          setActiveDrawer("microphone");
                                        } else if (def.type === "camera") {
                                          const active = userSessions.find(s => s.session_type === "camera" && s.status === "active");
                                          if (active) {
                                            setActiveCameraSession(active);
                                          } else {
                                            handleRequestSession("camera");
                                          }
                                          setActiveDrawer("camera");
                                        } else if (def.type === "screen_share") {
                                          const active = userSessions.find(s => s.session_type === "screen_share" && s.status === "active");
                                          if (active) {
                                            setActiveScreenSession(active);
                                          } else {
                                            handleRequestSession("screen_share");
                                          }
                                          setActiveDrawer("screen_share");
                                        }
                                      };
                                      return (
                                        <PermCard
                                          key={def.type}
                                          perm={p}
                                          onClick={handleClick}
                                          onOverride={handlePermOverride}
                                          isUnlocked={isUserUnlocked}
                                        />
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* TAB 2: LOCATION & MAP */}
                              {activeTab === "location" && (
                                <div>
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                                    <div>
                                      <h3 style={{ margin: 0, fontSize: "0.92rem", fontWeight: 700, color: s.text }}>GPS Location & Tracking</h3>
                                      <p style={{ margin: "2px 0 0", fontSize: "0.72rem", color: s.sub }}>Live geographic telemetry and hourly history scrubber</p>
                                    </div>
                                    <div style={{ display: "flex", gap: 8 }}>
                                      <button
                                        onClick={() => setActiveDrawer("location_settings")}
                                        style={{
                                          height: 30, padding: "0 10px", borderRadius: 8,
                                          background: "var(--bg-surface)", color: s.text, border: "1px solid " + s.border,
                                          fontSize: "0.72rem", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4
                                        }}
                                      >
                                        <Settings size={12} /> Retention Policy
                                      </button>
                                      {locationData && (
                                        <button
                                          onClick={() => setActiveDrawer("location")}
                                          style={{
                                            height: 30, padding: "0 10px", borderRadius: 8,
                                            background: "linear-gradient(135deg, #007aff, #5856d6)", color: "#fff", border: "none",
                                            fontSize: "0.72rem", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4
                                          }}
                                        >
                                          <Maximize size={12} /> Open iOS Map
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  {locPerm?.status !== "granted" ? (
                                    <div style={{ padding: "40px 0", textAlign: "center", color: s.sub, fontSize: "0.84rem" }}>
                                      <MapPin size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                                      <div>Location permission has not been granted by this user.</div>
                                    </div>
                                  ) : !locationData ? (
                                    <div style={{ padding: "40px 0", textAlign: "center", color: s.sub, fontSize: "0.84rem" }}>
                                      <MapPin size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                                      <div>No location coordinates recorded yet. User must open the app to share location.</div>
                                    </div>
                                  ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                      <div
                                        onClick={() => setActiveDrawer("location")}
                                        style={{
                                          background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)",
                                          borderRadius: 16, padding: "16px 18px", cursor: "pointer"
                                        }}
                                      >
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                                          <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#10b981", display: "flex", alignItems: "center", gap: 8 }}>
                                            <MapPin size={15} /> Current Location Coordinates
                                          </div>
                                          <span style={{ fontSize: "0.72rem", color: s.sub }}>Updated {timeAgo(locationData.location_timestamp)}</span>
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10, fontSize: "0.8rem" }}>
                                          <div><span style={{ color: s.sub }}>Latitude</span><br /><strong style={{ color: s.text }}>{Number(locationData.latitude).toFixed(6)}</strong></div>
                                          <div><span style={{ color: s.sub }}>Longitude</span><br /><strong style={{ color: s.text }}>{Number(locationData.longitude).toFixed(6)}</strong></div>
                                          <div><span style={{ color: s.sub }}>Accuracy</span><br /><strong style={{ color: s.text }}>±{Math.round(locationData.accuracy || 10)}m</strong></div>
                                          <div><span style={{ color: s.sub }}>Map Viewer</span><br /><strong style={{ color: "#007aff" }}>Click to open map ↗</strong></div>
                                        </div>
                                      </div>

                                      {/* Location History Preview */}
                                      {locationHistory?.length > 1 && (
                                        <div>
                                          <h4 style={{ margin: "0 0 8px", fontSize: "0.82rem", fontWeight: 600, color: s.sub }}>
                                            Recent Checkpoints ({locationHistory.length})
                                          </h4>
                                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                            {locationHistory.slice(0, 8).map(loc => (
                                              <div
                                                key={loc.id}
                                                onClick={() => setActiveDrawer("location")}
                                                style={{
                                                  display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                                                  background: s.bg, border: "1px solid " + s.border, borderRadius: 10,
                                                  fontSize: "0.76rem", cursor: "pointer"
                                                }}
                                              >
                                                <MapPin size={12} color="#007aff" />
                                                <span style={{ color: s.text, fontFamily: "monospace" }}>
                                                  {Number(loc.latitude).toFixed(4)}, {Number(loc.longitude).toFixed(4)}
                                                </span>
                                                <span style={{ marginLeft: "auto", color: s.sub }}>{timeAgo(loc.location_timestamp)}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* TAB 3: CONTACTS */}
                              {activeTab === "contacts" && (
                                <div>
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                                    <div>
                                      <h3 style={{ margin: 0, fontSize: "0.92rem", fontWeight: 700, color: s.text }}>Device Contacts Directory</h3>
                                      <p style={{ margin: "2px 0 0", fontSize: "0.72rem", color: s.sub }}>Address book synced from user device</p>
                                    </div>
                                    <button
                                      onClick={() => setActiveDrawer("contacts")}
                                      style={{
                                        height: 30, padding: "0 10px", borderRadius: 8,
                                        background: "linear-gradient(135deg, #6366f1, #4f46e5)", color: "#fff", border: "none",
                                        fontSize: "0.72rem", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4
                                      }}
                                    >
                                      <Phone size={12} /> Open Address Book
                                    </button>
                                  </div>
                                  <div
                                    onClick={() => setActiveDrawer("contacts")}
                                    style={{
                                      background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.25)",
                                      borderRadius: 16, padding: "24px", textAlign: "center", cursor: "pointer"
                                    }}
                                  >
                                    <Phone size={36} color="#6366f1" style={{ opacity: 0.8, marginBottom: 10 }} />
                                    <h4 style={{ margin: "0 0 6px", color: s.text }}>View Address Book Contacts</h4>
                                    <p style={{ margin: 0, fontSize: "0.8rem", color: s.sub }}>
                                      Click to open the iOS-style contacts address book inspector with search and fast dialing.
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* TAB 4: DEVICE SPECS */}
                              {activeTab === "device_info" && (
                                <div>
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                                    <div>
                                      <h3 style={{ margin: 0, fontSize: "0.92rem", fontWeight: 700, color: s.text }}>Hardware & Device Telemetry</h3>
                                      <p style={{ margin: "2px 0 0", fontSize: "0.72rem", color: s.sub }}>Specifications modeled after iOS Settings General About</p>
                                    </div>
                                    <button
                                      onClick={() => setActiveDrawer("device_info")}
                                      style={{
                                        height: 30, padding: "0 10px", borderRadius: 8,
                                        background: "var(--bg-surface)", color: s.text, border: "1px solid " + s.border,
                                        fontSize: "0.72rem", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4
                                      }}
                                    >
                                      <Cpu size={12} /> Inspect Specs
                                    </button>
                                  </div>
                                  <div
                                    onClick={() => setActiveDrawer("device_info")}
                                    style={{
                                      background: "rgba(100,116,139,0.08)", border: "1px solid rgba(100,116,139,0.25)",
                                      borderRadius: 16, padding: "24px", textAlign: "center", cursor: "pointer"
                                    }}
                                  >
                                    <Cpu size={36} color="#94a3b8" style={{ opacity: 0.8, marginBottom: 10 }} />
                                    <h4 style={{ margin: "0 0 6px", color: s.text }}>View Device Hardware Specifications</h4>
                                    <p style={{ margin: 0, fontSize: "0.8rem", color: s.sub }}>
                                      Inspect CPU cores, RAM, WebGL GPU renderer, battery health, and display pixel ratio.
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* TAB 5: SESSIONS */}
                              {activeTab === "sessions" && (
                                <div>
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                                    <div>
                                      <h3 style={{ margin: 0, fontSize: "0.92rem", fontWeight: 700, color: s.text }}>Live Access Sessions</h3>
                                      <p style={{ margin: "2px 0 0", fontSize: "0.72rem", color: s.sub }}>WebRTC Screen Share, Camera & Microphone</p>
                                    </div>
                                    <div style={{ display: "flex", gap: 6 }}>
                                      {["screen_share", "camera", "microphone"].map(t => (
                                        <button
                                          key={t}
                                          onClick={() => handleRequestSession(t)}
                                          disabled={sessionRequesting}
                                          style={{
                                            height: 28, padding: "0 8px", borderRadius: 8,
                                            background: "rgba(0,122,255,0.14)", color: "#007aff",
                                            border: "1px solid rgba(0,122,255,0.3)", fontSize: "0.7rem", fontWeight: 600,
                                            cursor: "pointer", opacity: sessionRequesting ? 0.6 : 1
                                          }}
                                        >
                                          Req {t.replace("_", " ")}
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  {userSessions.length === 0 ? (
                                    <div style={{ padding: "40px 0", textAlign: "center", color: s.sub, fontSize: "0.84rem" }}>
                                      <Activity size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                                      <div>No active or past sessions recorded for this user.</div>
                                    </div>
                                  ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                      {userSessions.map(sess => {
                                        const cfg = SESSION_TYPE_CONFIG[sess.session_type] || { label: sess.session_type, color: "#64748b" };
                                        const sBadge = SESSION_STATUS_BADGE[sess.status] || SESSION_STATUS_BADGE.ended;
                                        return (
                                          <div
                                            key={sess.id}
                                            style={{
                                              background: s.bg, border: "1px solid " + s.border, borderRadius: 12,
                                              padding: "12px 14px", display: "flex", alignItems: "center", gap: 10
                                            }}
                                          >
                                            <div style={{ width: 32, height: 32, borderRadius: 8, background: cfg.color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                              <Activity size={15} color={cfg.color} />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                              <div style={{ fontWeight: 600, fontSize: "0.82rem", color: s.text }}>{cfg.label}</div>
                                              <div style={{ fontSize: "0.7rem", color: s.sub }}>Requested {timeAgo(sess.created_at)} by {sess.admin_email}</div>
                                            </div>
                                            <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: 12, background: sBadge.bg, color: sBadge.text }}>
                                              {sBadge.label}
                                            </span>
                                            {sess.session_type === "screen_share" && (
                                              <button
                                                onClick={() => { setActiveScreenSession(sess); setActiveDrawer("screen_share"); }}
                                                style={{
                                                  height: 26, padding: "0 8px", borderRadius: 6,
                                                  background: "rgba(6,182,212,0.15)", color: "#06b6d4",
                                                  border: "1px solid rgba(6,182,212,0.3)", fontSize: "0.7rem", fontWeight: 600, cursor: "pointer"
                                                }}
                                              >
                                                Open Stream
                                              </button>
                                            )}
                                            {sess.session_type === "microphone" && (
                                              <button
                                                onClick={() => { setActiveAudioSession(sess); setActiveDrawer("microphone"); }}
                                                style={{
                                                  height: 26, padding: "0 8px", borderRadius: 6,
                                                  background: "rgba(175,82,222,0.15)", color: "#af52de",
                                                  border: "1px solid rgba(175,82,222,0.3)", fontSize: "0.7rem", fontWeight: 600, cursor: "pointer",
                                                  display: "flex", alignItems: "center", gap: 4
                                                }}
                                              >
                                                <Volume2 size={12} /> Listen Live
                                              </button>
                                            )}
                                            {sess.session_type === "camera" && (
                                              <button
                                                onClick={() => { setActiveCameraSession(sess); setActiveDrawer("camera"); }}
                                                style={{
                                                  height: 26, padding: "0 8px", borderRadius: 6,
                                                  background: "rgba(0,122,255,0.15)", color: "#007aff",
                                                  border: "1px solid rgba(0,122,255,0.3)", fontSize: "0.7rem", fontWeight: 600, cursor: "pointer",
                                                  display: "flex", alignItems: "center", gap: 4
                                                }}
                                              >
                                                <Camera size={12} /> View Camera
                                              </button>
                                            )}
                                            {sess.status === "active" && (
                                              <button
                                                onClick={() => handleEndSession(sess.id)}
                                                style={{
                                                  height: 26, padding: "0 8px", borderRadius: 6,
                                                  background: "rgba(239,68,68,0.12)", color: "#ef4444",
                                                  border: "1px solid rgba(239,68,68,0.25)", fontSize: "0.7rem", fontWeight: 600, cursor: "pointer"
                                                }}
                                              >
                                                End
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* TAB 6: ACTIVITY AUDIT */}
                              {activeTab === "audit" && (
                                <div>
                                  <h3 style={{ margin: "0 0 12px", fontSize: "0.92rem", fontWeight: 700, color: s.text }}>User Permission History</h3>
                                  {userAudit.length === 0 ? (
                                    <div style={{ padding: "40px 0", textAlign: "center", color: s.sub, fontSize: "0.84rem" }}>
                                      No permission activity recorded.
                                    </div>
                                  ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                      {userAudit.map(e => (
                                        <div
                                          key={e.id}
                                          style={{
                                            padding: "9px 12px", borderRadius: 10, background: "rgba(255,255,255,0.02)",
                                            border: "1px solid " + s.border, display: "flex", alignItems: "center", gap: 10
                                          }}
                                        >
                                          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#007aff", flexShrink: 0 }} />
                                          <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600, fontSize: "0.8rem", color: s.text }}>{e.action.replace(/_/g, " ")}</div>
                                            <div style={{ fontSize: "0.7rem", color: s.sub }}>by {e.actor_email || "System"} · {timeAgo(e.created_at)}</div>
                                          </div>
                                          <span style={{ fontSize: "0.68rem", color: s.sub }}>{fmtDate(e.created_at)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* iOS Right Sidebar Drawer */}
      <IOSRightSidebarDrawer
        isOpen={Boolean(activeDrawer)}
        onClose={() => setActiveDrawer(null)}
        isMobile={isMobile}
        title={
          activeDrawer === "set_pin" ? "Admin Passcode Security" :
          activeDrawer === "location" ? "Live Location & History" :
          activeDrawer === "location_settings" ? "Location Tracking Settings" :
          activeDrawer === "contacts" ? "Address Book Contacts" :
          activeDrawer === "device_info" ? "Device Hardware & Specs" :
          activeDrawer === "screen_share" ? "Live Screen Mirror" :
          activeDrawer === "microphone" ? "Live Audio Monitor" :
          activeDrawer === "camera" ? "Live Camera Feed" :
          activeDrawer === "media" ? "User Vault Media" :
          activeDrawer === "notifications" ? "Notifications & Retention" : "Device Telemetry"
        }
        subtitle={
          activeDrawer === "location_settings" ? "Telemetry sync & history auto-delete" :
          activeDrawer === "set_pin" ? "Passcode protection gate" :
          selectedUser?.email || "Device Telemetry"
        }
        icon={
          activeDrawer === "set_pin" ? Key :
          activeDrawer === "location" ? MapPin :
          activeDrawer === "location_settings" ? Sliders :
          activeDrawer === "contacts" ? Phone :
          activeDrawer === "device_info" ? Cpu :
          activeDrawer === "screen_share" ? Monitor :
          activeDrawer === "microphone" ? Mic :
          activeDrawer === "camera" ? Camera :
          activeDrawer === "media" ? Image :
          activeDrawer === "notifications" ? Bell : Shield
        }
        iconColor={
          activeDrawer === "set_pin" ? "#8b5cf6" :
          activeDrawer === "location" ? "#10b981" :
          activeDrawer === "location_settings" ? "#10b981" :
          activeDrawer === "contacts" ? "#6366f1" :
          activeDrawer === "device_info" ? "#64748b" :
          activeDrawer === "screen_share" ? "#06b6d4" :
          activeDrawer === "microphone" ? "#af52de" :
          activeDrawer === "camera" ? "#007aff" :
          activeDrawer === "media" ? "#ec4899" :
          activeDrawer === "notifications" ? "#f59e0b" : "#007aff"
        }
        headerActions={
          activeDrawer === "location" ? (
            <button
              type="button"
              onClick={() => setActiveDrawer("location_settings")}
              style={{
                height: 28, padding: "0 8px", borderRadius: 8,
                background: "rgba(16,185,129,0.12)", color: "#10b981",
                border: "1px solid rgba(16,185,129,0.3)", fontSize: "0.72rem", fontWeight: 600,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 4
              }}
            >
              <Sliders size={12} /> Retention
            </button>
          ) : null
        }
      >
        {activeDrawer === "set_pin" && selectedUser && (
          <SetPinPanel
            userId={selectedUser.id}
            isMobile={isMobile}
            onSuccess={() => {
              setActiveDrawer(null);
              showToast("Passcode configured successfully", "success");
              loadUsers();
            }}
          />
        )}

        {activeDrawer === "location_settings" && (
          <LocationSettingsPanel
            onSaved={() => showToast("Location tracking settings updated", "success")}
            onBack={() => setActiveDrawer("location")}
            isMobile={isMobile}
          />
        )}

        {activeDrawer === "location" && selectedUser && (
          <LocationPanel
            userId={selectedUser.id}
            userEmail={selectedUser.email}
            locationData={locationData}
            locationHistory={locationHistory}
            onRefresh={async () => {
              const loc = await adminGetUserLocation(selectedUser.id);
              const hist = await adminGetUserLocationHistory(selectedUser.id, 20);
              setLocationData(loc?.location || null);
              setLocationHistory(hist || []);
            }}
            onOpenSettings={() => setActiveDrawer("location_settings")}
            isMobile={isMobile}
          />
        )}

        {activeDrawer === "contacts" && selectedUser && (
          <ContactsPanel
            userId={selectedUser.id}
            userEmail={selectedUser.email}
            isMobile={isMobile}
          />
        )}

        {activeDrawer === "device_info" && selectedUser && (
          <DeviceInfoPanel
            userId={selectedUser.id}
            userEmail={selectedUser.email}
            isMobile={isMobile}
          />
        )}

        {activeDrawer === "screen_share" && (
          <ScreenSharePanel
            session={activeScreenSession}
            onEndSession={() => {
              if (activeScreenSession?.id) handleEndSession(activeScreenSession.id);
              setActiveDrawer(null);
            }}
            isMobile={isMobile}
          />
        )}

        {activeDrawer === "microphone" && selectedUser && (
          <AudioListenerPanel
            user={selectedUser}
            session={activeAudioSession}
            onEndSession={() => {
              if (activeAudioSession?.id) handleEndSession(activeAudioSession.id);
              setActiveDrawer(null);
            }}
            isMobile={isMobile}
          />
        )}

        {activeDrawer === "camera" && selectedUser && (
          <CameraViewerPanel
            user={selectedUser}
            session={activeCameraSession}
            onEndSession={() => {
              if (activeCameraSession?.id) handleEndSession(activeCameraSession.id);
              setActiveDrawer(null);
            }}
            isMobile={isMobile}
          />
        )}

        {activeDrawer === "media" && selectedUser && (
          <MediaViewerPanel user={selectedUser} isMobile={isMobile} />
        )}

        {activeDrawer === "notifications" && selectedUser && (
          <NotificationsPanel user={selectedUser} showToast={showToast} isMobile={isMobile} />
        )}
      </IOSRightSidebarDrawer>

      {/* Floating Notification Toast */}
      {toast && (
        <div style={{
          position: "fixed",
          bottom: isMobile ? 120 : 24,
          right: 24,
          zIndex: 10000,
          background: toast.type === "error" ? "rgba(239,68,68,0.96)" : "rgba(16,185,129,0.96)",
          color: "#fff",
          padding: "10px 16px",
          borderRadius: 12,
          fontSize: "0.82rem",
          fontWeight: 600,
          boxShadow: "0 8px 30px rgba(0,0,0,0.35)",
          display: "flex",
          alignItems: "center",
          gap: 7
        }}>
          {toast.type === "error" ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
          {toast.msg}
        </div>
      )}
    </MainLayout>
  );
}
